'use client';

/**
 * USER DOCS: changes to this component should be accompanied by a
 * corresponding update to the user guide:
 * next_portal/user-docs/content/docs/total-connect/email.mdx
 * (published at user-docs.totalaccess.co.za/total-connect/email)
 */

import UserDocsLink from '@/components/shared/UserDocsLink';
import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useEmailStore } from '../stores/emailStore';
import emailApiService from '../services/emailApi.service';
import { useAccessibleEmailClients } from '@/hooks/useAccessibleEmailClients';
import { TotalConnectAccessGate } from '@/components/TotalConnectAccessGate';
import ThreadList from './ThreadList';
import ThreadViewer from './ThreadViewer';
import OutboxList from './OutboxList';
import MultiComposeManager, { useMultiCompose, type ComposerInstance, type FromAccount } from './MultiComposeManager';
import EmailSettingsModal from './EmailSettingsModal';
import type { ThreadListItem, Email, EmailAddress, ThreadMessage } from '../types';
import {
  Inbox, Send, FileText, Trash2, Star, Archive, AlertCircle, Ban, Clock,
  RefreshCw, Search, Plus, Settings, Calendar, Video, MessageCircle,
  ChevronDown, ChevronRight, ChevronLeft, FolderOpen, Mail, Users, X, ArrowUpDown, CheckCircle, Building2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { useMasterSync } from '@/lib/useMasterSync';
import { isCapacitor } from '@/lib/platformUtils';
import { Capacitor, type PluginListenerHandle } from '@capacitor/core';
import { App } from '@capacitor/app';
import { isBiometricLockActive, waitForBiometricUnlock } from '@/lib/biometricLockState';
import { notify } from '@/lib/notifications';
import { authService } from '@/lib/authService';
import {
  hydrateEmailConnectPrefs,
  mergeAndPersistEmailConnectPrefs,
  TOTAL_CONNECT_EMAIL_PREFS_UPDATED_EVENT,
  type EmailConnectUiPrefs,
} from '@/lib/offline/emailConnectPreferencesStore';
import { primeThreadFolderCache } from '../utils/primeThreadFolderCache';
import { haptics } from '@/lib/haptics';
import { decodeMimeHeader } from '@/lib/mimeHeader';
import { parseEmailString, parseEmailRecipients as parseEmailRecipientsUtil } from '../utils/emailViewerUtils';
import { isSilentFetchError } from '@/lib/capacitorFetchPatch';

function strFromUnknown(v: unknown): string {
  return typeof v === 'string' ? v : '';
}

// Helper to normalize email field (from/to/cc) to have both email and address properties
const normalizeEmailField = (field: unknown): EmailAddress | undefined => {
  if (field == null) return undefined;
  if (typeof field === 'string') {
    const parsed = parseEmailString(field);
    return { email: parsed.email || parsed.address || field, address: parsed.address || parsed.email || field, name: parsed.name || field.split('@')[0] };
  }
  if (typeof field !== 'object') return undefined;
  const o = field as Record<string, unknown>;
  const email = strFromUnknown(o.email) || strFromUnknown(o.address);
  const address = strFromUnknown(o.address) || strFromUnknown(o.email);
  if (!email && !address) return undefined;
  const nameRaw = o.name;
  const name =
    decodeMimeHeader(typeof nameRaw === 'string' ? nameRaw : '') || (email || address).split('@')[0];
  return { name, email: email || address, address: address || email };
};

const normalizeEmailRecipients = (recipients: unknown): EmailAddress[] => {
  if (recipients == null) return [];
  if (typeof recipients === 'string') {
    return parseEmailRecipientsUtil(recipients) as EmailAddress[];
  }
  if (Array.isArray(recipients)) {
    return recipients
      .map((r) => normalizeEmailField(r))
      .filter((x): x is EmailAddress => x != null);
  }
  const normalized = normalizeEmailField(recipients);
  return normalized ? [normalized] : [];
};

// Build the full email chain HTML from thread messages below the current message
const buildThreadChainHtml = (messages: ThreadMessage[], currentMessageId?: string): string => {
  if (!messages || messages.length <= 1) return '';
  
  // Find the index of the current message in the thread
  let currentIndex = messages.length - 1;
  if (currentMessageId) {
    const found = messages.findIndex(m => m.message_id === currentMessageId);
    if (found >= 0) currentIndex = found;
  }
  
  // Get all messages BEFORE the current one (the chain/history), oldest first
  const priorMessages = messages.slice(0, currentIndex);
  if (priorMessages.length === 0) return '';
  
  // Build chain from most recent to oldest (reverse order for natural reading)
  const chainParts = priorMessages.reverse().map(msg => {
    const from = msg.from as EmailAddress | string | undefined;
    const fromName = typeof from === 'string' ? from : (from?.name || from?.email || from?.address || 'Unknown');
    const fromEmail = typeof from === 'string' ? from : (from?.email || from?.address || '');
    const msgDate = msg.date ? new Date(msg.date).toLocaleString() : 'Unknown date';
    const msgBody = msg.html_content || msg.body_html || msg.text_content || msg.body_text || '';
    
    return `<br><p>On ${msgDate}, ${fromName} &lt;${fromEmail}&gt; wrote:</p>
      <blockquote style="border-left: 2px solid #ccc; padding-left: 10px; margin-left: 0; color: #666;">
        ${msgBody}
      </blockquote>`;
  });
  
  return chainParts.join('');
};

const buildComposerEmailFromMessage = (
  message: ThreadMessage,
  thread: ThreadListItem | null,
  html: string,
  text: string
): Email => ({
  id: message.message_id,
  messageId: message.message_id,
  threadId: thread?.thread_id,
  folder: thread?.folder || 'INBOX',
  from: normalizeEmailField(message.from),
  to: normalizeEmailRecipients(message.to),
  cc: normalizeEmailRecipients(message.cc),
  subject: decodeMimeHeader(thread?.subject || message.subject || '') || '(No subject)',
  body: text,
  text,
  html,
  date: new Date(message.date || Date.now()),
  read: !!message.is_read,
  starred: !!message.is_starred,
  attachments: message.attachments,
});

/** Standard IMAP folders + icons (module scope so useMemo dependencies stay stable) */
const STANDARD_EMAIL_FOLDERS = [
  { name: 'INBOX', label: 'Inbox', icon: Inbox },
  { name: 'Junk', label: 'Junk', icon: Ban },
  { name: 'Trash', label: 'Trash', icon: Trash2 },
  { name: 'Archive', label: 'Archive', icon: Archive },
  { name: 'Sent', label: 'Sent', icon: Send },
  { name: 'Drafts', label: 'Drafts', icon: FileText },
  { name: 'Starred', label: 'Starred', icon: Star },
  { name: 'Spam', label: 'Spam', icon: AlertCircle },
  { name: 'Outbox', label: 'Outbox', icon: Clock },
  { name: 'Scheduled', label: 'Scheduled', icon: Calendar },
];

function normalizeFolderKey(name?: string | null): string {
  return String(name ?? '')
    .trim()
    .toLowerCase();
}

/** Fast local thread filter while the debounced MongoDB search request is in flight */
function threadMatchesQuickSearch(thread: ThreadListItem, needle: string): boolean {
  const q = needle.trim().toLowerCase();
  if (q.length < 2) return true;
  if (decodeMimeHeader(String(thread.subject || '')).toLowerCase().includes(q)) return true;
  if (
    thread.participants?.some((p) => {
      const addr = (p.address || p.email || '').toLowerCase();
      const name = decodeMimeHeader(p.name || '').toLowerCase();
      return addr.includes(q) || name.includes(q);
    })
  ) {
    return true;
  }
  if (decodeMimeHeader(String(thread.last_message_preview || '')).toLowerCase().includes(q)) return true;
  if (
    thread.messages?.some((m) => {
      const text = String(m.body_text || m.snippet || '').toLowerCase();
      return text.includes(q);
    })
  ) {
    return true;
  }
  return false;
}

type PendingEmailOpenContext = {
  emailId?: string;
  threadId?: string;
  folder?: string;
  accountId?: number;
  clientId?: number;
  timestamp?: number;
};

type NotificationThreadLookup = {
  thread_id: string;
  subject?: string;
  participants?: EmailAddress[];
  folder?: string;
  labels?: string[];
  has_unread?: boolean;
  unread_count?: number;
  starred?: boolean;
  message_count?: number;
  messages?: ThreadMessage[];
  last_message_at?: string;
  read_by_users?: number[];
  account_id?: number;
};

function normalizeOptionalPositiveNumber(value: unknown): number | undefined {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : undefined;
}

function normalizePendingEmailTarget(raw: unknown): PendingEmailOpenContext | null {
  if (!raw || typeof raw !== 'object') return null;

  const value = raw as Record<string, unknown>;
  const emailId = strFromUnknown(value.emailId) || strFromUnknown(value.messageId);
  const threadId = strFromUnknown(value.threadId);

  if (!emailId && !threadId) {
    return null;
  }

  return {
    emailId: emailId || undefined,
    threadId: threadId || undefined,
    folder: strFromUnknown(value.folder) || 'INBOX',
    accountId: normalizeOptionalPositiveNumber(value.accountId ?? value.account_id ?? value.mailboxId ?? value.mailbox_id),
    clientId: normalizeOptionalPositiveNumber(value.clientId ?? value.client_id),
    timestamp: normalizeOptionalPositiveNumber(value.timestamp) || Date.now(),
  };
}

function buildThreadListItemFromNotificationThread(thread: NotificationThreadLookup): ThreadListItem {
  const lastMessage = thread.messages?.[thread.messages.length - 1];
  const lastMessageFrom = normalizeEmailField(lastMessage?.from) || thread.participants?.[0];

  return {
    thread_id: thread.thread_id,
    subject: decodeMimeHeader(thread.subject || '') || '(No subject)',
    participants: thread.participants || [],
    last_message_at: thread.last_message_at || lastMessage?.date || new Date().toISOString(),
    last_message_preview: lastMessage?.snippet || lastMessage?.text_content || '',
    last_message_from: lastMessageFrom,
    folder: thread.folder || 'INBOX',
    labels: thread.labels || [],
    has_unread: !!thread.has_unread,
    unread_count: thread.unread_count || 0,
    starred: !!thread.starred,
    is_starred: !!thread.starred,
    message_count: thread.message_count || thread.messages?.length || 1,
    messages: thread.messages,
    read_by_users: thread.read_by_users,
    account_id: thread.account_id,
  };
}

export type EmailRealtimeStatus = 'live' | 'online' | 'offline';

export interface EmailStatusInfo {
  threadCount: number;
  wsConnected: boolean;
  isOnline: boolean;
  realtimeStatus: EmailRealtimeStatus;
  isSearching: boolean;
  selectedCount: number;
  isSelecting: boolean;
}

type DelegatedMailboxAccount = {
  mailboxId: number;
  emailAddress: string;
  displayName: string;
  permissions: {
    canSend: boolean;
    canSendAs: boolean;
    canSendOnBehalf: boolean;
  };
  unreadCount?: number;
  isShared?: boolean;
  isPartnerAccount?: boolean;
  partnerClientId?: number;
  partnerClientName?: string;
  isTeamMailbox?: boolean;
  teamId?: string | number | null;
  teamName?: string | null;
  teamColor?: string | null;
  forceConnectorSmtp?: boolean;
  defaultSendAs?: string | null;
  linkedConnector?: {
    id: number;
    name: string;
    email: string;
    deliverabilityScore?: number | null;
    hasDeliveryIssues?: boolean;
  } | null;
  aliases?: Array<{
    id: number;
    email: string;
    type: string;
  }>;
};

type SharedMailboxFolder = {
  id: string | number;
  name: string;
  path: string;
  displayName: string;
  color?: string;
  isSystem?: boolean;
  type?: string;
  isCustom?: boolean;
};

type MailboxOption = {
  contextId: string;
  mailboxId: number | null;
  email: string;
  label: string;
  type: 'primary' | 'shared' | 'combined' | 'personal-cross-client';
  unreadCount: number;
  isPartnerAccount?: boolean;
  partnerClientName?: string;
  /** Client name for cross-client personal mailboxes. */
  clientName?: string;
  /** Client ID for cross-client personal mailboxes. */
  clientId?: number;
  badgeLabel?: string;
  icon: typeof Mail;
};

interface EnhancedEmailModuleProps {
  clientId: number;
  userId: number;
  delegatedAccounts?: DelegatedMailboxAccount[];
  manageStoreLifecycle?: boolean;
  onNavigateToCalendar?: () => void;
  onNavigateToMeetings?: () => void;
  onNavigateToChat?: () => void;
  /** Hide Calendar / Meetings / Chat shortcuts in the folder sidebar (e.g. standalone suite rail duplicates them). */
  hideCrossModuleSidebarShortcuts?: boolean;
  isModal?: boolean; // When true, use mobile-friendly layout
  initialEmailId?: string; // Email ID to open on cold start
  onEmailOpened?: () => void; // Callback when initial email is opened
  // Props for embedding in page.tsx without internal sidebar/composer
  hideSidebar?: boolean; // Hide folder sidebar (when using external navigation like UnifiedSideNav)
  hideComposer?: boolean; // Don't render internal composer (when using external MultiComposeManager)
  onExternalCompose?: () => void; // Callback for compose button when using external composer
  onExternalReply?: (email: Email, replyAll?: boolean) => void;
  onExternalForward?: (email: Email) => void;
  externalFolder?: string; // External folder selection (synced from page.tsx state)
  onFolderChange?: (folder: string) => void; // Callback when folder changes internally
  listPanelWidth?: number; // Width of list panel (for resizable layout)
  onDoubleClickThread?: (thread: ThreadListItem) => void;
  /** Callback to view work item in tasks panel (from EmailToTaskModal) */
  onViewWorkItemInTasks?: (workItemId: string) => void;
  /** Callback when a thread is selected (used by EmailMobileModal for task creation) */
  onThreadSelected?: (threadId: string) => void;
  /** Callback when user edits a PDF and wants to reply with it attached */
  onReplyWithAttachment?: (email: Email, file: File) => void;
  /** Callback reporting status info (thread count, connection, search state) for parent header */
  onStatusInfo?: (info: EmailStatusInfo) => void;
  /** Incrementing signal from parents that want to clear the current multi-select state */
  clearMultiSelectSignal?: number;
  /** When embedded in EmailMobileModal, exposes compose / sync / settings for the chrome header */
  onModalChromeReady?: (
    api: {
      sync: () => Promise<void>;
      openSettings: () => void;
      compose: () => void;
      listView: boolean;
      search: { value: string; onChange: (v: string) => void };
      filters: {
        options: { value: string; label: string }[];
        value: string;
        onChange: (v: string) => void;
        dropdownLabel?: string;
      };
      sort: {
        options: { value: string; label: string }[];
        value: string;
        onChange: (v: string) => void;
      };
    } | null,
  ) => void;
  /** Hides the module's built-in mobile list toolbar when the parent renders
   * a shared ModalFilterBar in the modal chrome instead. */
  suppressListToolbar?: boolean;
}

export default function EnhancedEmailModule({
  clientId,
  userId,
  delegatedAccounts: delegatedAccountsProp,
  manageStoreLifecycle = true,
  onNavigateToCalendar,
  onNavigateToMeetings,
  onNavigateToChat,
  hideCrossModuleSidebarShortcuts = false,
  isModal = false,
  initialEmailId,
  onEmailOpened,
  // External control props
  hideSidebar = false,
  hideComposer = false,
  onExternalCompose,
  onExternalReply,
  onExternalForward,
  externalFolder,
  onFolderChange,
  listPanelWidth: externalListPanelWidth,
  onDoubleClickThread: externalDoubleClickThread,
  onViewWorkItemInTasks,
  onThreadSelected,
  onReplyWithAttachment: externalReplyWithAttachment,
  onStatusInfo,
  clearMultiSelectSignal,
  onModalChromeReady,
  suppressListToolbar = false,
}: EnhancedEmailModuleProps) {
  const {
    folders,
    selectedFolder,
    error,
    hasEmailAccount,
    primaryAccount,
    syncStatus,
    wsConnected,
    inboxUnreadCount,
    checkEmailAccount,
    fetchEmails,
    fetchFolders,
    selectFolder,
    connectWebSocket,
    disconnectWebSocket,
    // Threads support
    threads,
    selectedThread,
    threadsLoading,
    fetchThreads,
    smartSyncThreads,
    selectThread,
    deleteThread,
    deleteThreads,
    moveThread,
    loadMoreThreads,
    setThreadsSearchQuery,
    threadsHasMore,
    threadsSearchQuery,
    // Shared mailbox support
    activeSharedMailboxId,
    sharedMailboxUnreadCounts,
    setActiveSharedMailbox,
    setIncludeSharedMailboxThreads,
    setSharedMailboxUnreadCount,
    fetchThreadStats,
    // Outbox support
    outboxItems,
    outboxLoading,
    fetchOutbox,
    cancelOutboxItem,
  } = useEmailStore();

  // Cross-client email access — all mailboxes the user has across every client.
  // Used to render an Outlook-style multi-mailbox sidebar and to ensure
  // mailboxes from other clients are switchable regardless of the active client.
  const { allMailboxes: accessibleCrossClientMailboxes } = useAccessibleEmailClients();

  const [showComposer, setShowComposer] = useState(false);
  const [replyTo, setReplyTo] = useState<(ComposerInstance['replyTo'] & { isDraft?: boolean }) | null>(null);
  const [pendingComposerFromAccount, setPendingComposerFromAccount] = useState<FromAccount | null>(null);
  
  // Internal multi-compose (used when hideComposer=false)
  const { composers, activeComposerId, createComposer: internalCreateComposer, updateComposer, closeComposer, minimizeComposer, restoreComposer, maximizeComposer, handleSent: internalHandleSent } = useMultiCompose();
  
  // Bridge showComposer/replyTo state to MultiComposeManager when internal composer is used
  const lastComposerTrigger = useRef(0);
  useEffect(() => {
    if (showComposer && !hideComposer) {
      const now = Date.now();
      if (now - lastComposerTrigger.current > 200) {
        lastComposerTrigger.current = now;
        internalCreateComposer(replyTo || undefined);
        if (pendingComposerFromAccount) {
          internalCreateComposer(replyTo || undefined, pendingComposerFromAccount);
        } else {
          internalCreateComposer(replyTo || undefined);
        }
        setShowComposer(false);
        setPendingComposerFromAccount(null);
      }
    }
  }, [showComposer, hideComposer, replyTo, internalCreateComposer, pendingComposerFromAccount]);
  const [searchQuery, setSearchQuery] = useState(() => threadsSearchQuery);
  // Load sort preferences from localStorage
  const [sortBy, setSortBy] = useState<'date' | 'subject' | 'from'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('emailSortPrefs');
      if (saved) try { return JSON.parse(saved).sortBy || 'date'; } catch { return 'date'; }
    }
    return 'date';
  });
  const [sortOrder, setSortOrder] = useState<'desc' | 'asc'>(() => {
    if (typeof window !== 'undefined') {
      const saved = localStorage.getItem('emailSortPrefs');
      if (saved) try { return JSON.parse(saved).sortOrder || 'desc'; } catch { return 'desc'; }
    }
    return 'desc';
  });
  const [showSortDropdown, setShowSortDropdown] = useState(false);
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  // Show sync checkmark after successful sync (auto-hides after 3s)
  const [showSyncSuccess, setShowSyncSuccess] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [foldersExpanded, setFoldersExpanded] = useState(true);
  const [servicesExpanded, setServicesExpanded] = useState(true);
  const [showFolderDropdown, setShowFolderDropdown] = useState(false);
  const [showBulkMoveDropdown, setShowBulkMoveDropdown] = useState(false);
  const [isBulkMoving, setIsBulkMoving] = useState(false);
  const [mobileView, setMobileView] = useState<'list' | 'email'>('list');
  const [fullScreenThread, setFullScreenThread] = useState<ThreadListItem | null>(null);
  const [selectedThreadIds, setSelectedThreadIds] = useState<string[]>([]);
  const [multiSelectMode, setMultiSelectMode] = useState(false);
  const resolvedMobileView = isModal && mobileView === 'email' && !selectedThread ? 'list' : mobileView;
  const previousWsConnectedRef = useRef<boolean | null>(null);
  const clearMultiSelectSignalRef = useRef(clearMultiSelectSignal);

  useEffect(() => {
    if (resolvedMobileView !== mobileView) {
      setMobileView(resolvedMobileView);
    }
  }, [mobileView, resolvedMobileView]);
  
  // Refs for click-outside handling on mobile dropdowns
  const folderDropdownRef = useRef<HTMLDivElement>(null);
  const bulkMoveDropdownRef = useRef<HTMLDivElement>(null);
  const accountSelectorRef = useRef<HTMLDivElement>(null);
  const sortDropdownRef = useRef<HTMLDivElement>(null);
  
  // Shared mailbox support
  const [localDelegatedAccounts, setDelegatedAccounts] = useState<DelegatedMailboxAccount[]>([]);
  const delegatedAccounts = delegatedAccountsProp ?? localDelegatedAccounts;
  const [showAccountSelector, setShowAccountSelector] = useState(false);
  // Track whether email initialization is complete (prevents flash of "no account" screen)
  const [emailInitComplete, setEmailInitComplete] = useState(false);
  // Track whether delegated accounts store init has been done to avoid duplicate calls
  const delegatedStoreInitRef = useRef(false);
  const lastOpenedNotificationTargetRef = useRef<string | null>(null);
  const hasCheckedPendingEmailOnInitRef = useRef(false);
  const [sharedMailboxFolders, setSharedMailboxFolders] = useState<Record<number, SharedMailboxFolder[]>>({});
  const [mailboxesExpanded, setMailboxesExpanded] = useState(true);
  
  // Delegates for active shared mailbox (for read status display)
  const [delegates, setDelegates] = useState<Array<{
    userId: number;
    name: string;
    firstName?: string;
    avatarUrl?: string;
  }>>([]);

  // Load preferences
  const [compactView, setCompactView] = useState(false);
  const [showPreview, setShowPreview] = useState(true);
  const [combinedInboxEnabled, setCombinedInboxEnabled] = useState(true);
  const [nullMailboxMode, setNullMailboxMode] = useState<'primary' | 'combined'>('combined');
  const shouldShowCombinedMailboxOption = combinedInboxEnabled && delegatedAccounts.length > (hasEmailAccount ? 0 : 1);

  // MasterSync for unified sync across all modules
  const {
    isOnline: masterSyncOnline,
    triggerSync: triggerMasterSync,
  } = useMasterSync({
    autoSyncOnReconnect: true,
    backgroundSyncInterval: 5 * 60 * 1000 // 5 minutes
  });

  const openPendingEmailTarget = useCallback(async (rawTarget: PendingEmailOpenContext | null | undefined) => {
    const target = normalizePendingEmailTarget(rawTarget);
    if (!target) return;
    if (!emailInitComplete) return;
    if (!hasEmailAccount && !activeSharedMailboxId && !target.accountId) return;

    const targetKey = `${target.emailId || ''}:${target.threadId || ''}:${target.accountId || 0}:${target.folder || 'INBOX'}`;
    if (lastOpenedNotificationTargetRef.current === targetKey) {
      return;
    }

    try {
      let resolvedEmailId = target.emailId;
      let resolvedThreadId = target.threadId;
      let resolvedFolder = target.folder || 'INBOX';
      let resolvedAccountId = target.accountId;

      if (resolvedEmailId) {
        const emailResponse = await emailApiService.getEmail(resolvedEmailId, resolvedAccountId);
        if (emailResponse.success && emailResponse.data) {
          resolvedThreadId = resolvedThreadId || emailResponse.data.threadId || emailResponse.data.thread_id || resolvedEmailId;
          resolvedFolder = emailResponse.data.folder || resolvedFolder;
          resolvedAccountId = resolvedAccountId ?? normalizeOptionalPositiveNumber(emailResponse.data.account_id);
        }
      }

      const desiredSharedMailboxId = resolvedAccountId && resolvedAccountId !== primaryAccount?.id
        ? resolvedAccountId
        : null;

      if ((activeSharedMailboxId ?? null) !== desiredSharedMailboxId) {
        setActiveSharedMailbox(desiredSharedMailboxId);
      }
      setIncludeSharedMailboxThreads(false);

      selectFolder(resolvedFolder);
      await fetchThreads({
        folder: resolvedFolder,
        accountId: resolvedAccountId,
        forceRefresh: true,
      });

      let threadToOpen = resolvedThreadId
        ? useEmailStore.getState().threads.find((thread) => thread.thread_id === resolvedThreadId)
        : undefined;

      if (!threadToOpen && resolvedThreadId) {
        const threadResponse = await emailApiService.getThread(resolvedThreadId, resolvedAccountId);
        if (threadResponse.success && threadResponse.data?.thread) {
          threadToOpen = buildThreadListItemFromNotificationThread(
            threadResponse.data.thread as NotificationThreadLookup,
          );
        }
      }

      if (threadToOpen) {
        selectThread(threadToOpen);
        if (isModal) {
          setMobileView('email');
        }
      }

      if (typeof window !== 'undefined') {
        window.__hideNavigationUI?.();
      }

      if (typeof sessionStorage !== 'undefined') {
        sessionStorage.removeItem('pending_email_open');
      }

      lastOpenedNotificationTargetRef.current = targetKey;
      onEmailOpened?.();
    } catch (error) {
      console.error('[Email] Failed to open pending email target:', error, rawTarget);
    }
  }, [
    activeSharedMailboxId,
    emailInitComplete,
    fetchThreads,
    hasEmailAccount,
    isModal,
    onEmailOpened,
    primaryAccount?.id,
    selectFolder,
    selectThread,
    setActiveSharedMailbox,
    setIncludeSharedMailboxThreads,
  ]);

  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    void hydrateEmailConnectPrefs(clientId).then((prefs) => {
      if (cancelled) return;
      setCompactView(prefs.compactView);
      setShowPreview(prefs.showPreview !== false);
      setCombinedInboxEnabled(prefs.combinedInbox);
    });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onPrefsUpdated = (e: Event) => {
      const ce = e as CustomEvent<EmailConnectUiPrefs>;
      const d = ce.detail;
      if (!d) return;
      setCompactView(d.compactView);
      setShowPreview(d.showPreview !== false);
      setCombinedInboxEnabled(d.combinedInbox);
    };
    window.addEventListener(TOTAL_CONNECT_EMAIL_PREFS_UPDATED_EVENT, onPrefsUpdated);
    return () => window.removeEventListener(TOTAL_CONNECT_EMAIL_PREFS_UPDATED_EVENT, onPrefsUpdated);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const tokens = authService.getTokens();
        if (!tokens?.access_token || !clientId) return;
        const response = await fetch('/api/v1/total-connect/settings', {
          headers: { Authorization: `Bearer ${tokens.access_token}` },
        });
        if (!response.ok || cancelled) return;
        const data = await response.json();
        if (
          cancelled ||
          !data?.success ||
          !data.settings?.email ||
          typeof data.settings.email.combinedInbox !== 'boolean'
        ) {
          return;
        }
        const combinedInbox = data.settings.email.combinedInbox;
        setCombinedInboxEnabled(combinedInbox);
        await mergeAndPersistEmailConnectPrefs(clientId, { combinedInbox }, { emit: false });
      } catch {
        /* ignore */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Persist sort preferences to localStorage
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('emailSortPrefs', JSON.stringify({ sortBy, sortOrder }));
    }
  }, [sortBy, sortOrder]);

  const fetchDelegatedAccounts = useCallback(async (preloadedAccounts?: DelegatedMailboxAccount[]) => {
    try {
      let allAccounts: DelegatedMailboxAccount[];

      if (preloadedAccounts && preloadedAccounts.length > 0) {
        allAccounts = preloadedAccounts;
      } else {
        const tokens = authService.getTokens();
        if (!tokens?.access_token) return;

        const response = await fetch('/api/v1/email/delegated-accounts', {
          headers: {
            'Authorization': `Bearer ${tokens.access_token}`,
          },
        });

        const result: {
          success?: boolean;
          delegatedAccounts?: DelegatedMailboxAccount[];
          partnerAccounts?: unknown[];
        } = await response.json();
        if (!result.success || !result.delegatedAccounts) return;

        allAccounts = [...result.delegatedAccounts];
        if (Array.isArray(result.partnerAccounts)) {
          for (const pa of result.partnerAccounts) {
            if (pa != null && typeof pa === 'object') {
              allAccounts.push({
                ...(pa as DelegatedMailboxAccount),
                isShared: true,
                isPartnerAccount: true,
              });
            }
          }
        }
      }

      setDelegatedAccounts(allAccounts);
      // Initialize shared mailbox unread counts in the store
      allAccounts.forEach((account: { mailboxId: number; unreadCount?: number }) => {
        setSharedMailboxUnreadCount(account.mailboxId, account.unreadCount || 0);
      });

      const folderResults = await Promise.all(
        allAccounts.map(async (account: { mailboxId: number }) => {
          try {
            const foldersResponse = await emailApiService.getFolders(account.mailboxId);
            const mailboxFolders = foldersResponse.success && Array.isArray(foldersResponse.data)
              ? foldersResponse.data
                  .filter((folder) => !folder.isSystem && folder.type === 'custom')
                  .map((folder) => ({
                    id: folder.id,
                    name: folder.name,
                    path: folder.path,
                    displayName: folder.displayName || folder.name,
                    color: folder.color,
                  }))
              : [];

            return { mailboxId: account.mailboxId, folders: mailboxFolders };
          } catch (folderError) {
            console.error(`[EnhancedEmailModule] Error fetching folders for mailbox ${account.mailboxId}:`, folderError);
            return { mailboxId: account.mailboxId, folders: [] };
          }
        })
      );

      setSharedMailboxFolders(
        folderResults.reduce<Record<number, SharedMailboxFolder[]>>((acc, entry) => {
          acc[entry.mailboxId] = entry.folders;
          return acc;
        }, {})
      );
    } catch (error) {
      console.error('[EnhancedEmailModule] Error fetching delegated accounts:', error);
    }
  }, [setSharedMailboxUnreadCount]);

  // Fetch custom folders for cross-client personal mailboxes so the folder
  // sidebar reflects per-mailbox custom folders when switching to them.
  useEffect(() => {
    if (accessibleCrossClientMailboxes.length === 0) return;

    let cancelled = false;

    const fetchCrossClientFolders = async () => {
      const folderResults = await Promise.all(
        accessibleCrossClientMailboxes.map(async (mb) => {
          try {
            const foldersResponse = await emailApiService.getFolders(mb.mailbox_id);
            const mailboxFolders = foldersResponse.success && Array.isArray(foldersResponse.data)
              ? foldersResponse.data
                  .filter((folder) => !folder.isSystem && folder.type === 'custom')
                  .map((folder) => ({
                    id: folder.id,
                    name: folder.name,
                    path: folder.path,
                    displayName: folder.displayName || folder.name,
                    color: folder.color,
                  }))
              : [];
            return { mailboxId: mb.mailbox_id, folders: mailboxFolders };
          } catch (folderError) {
            console.error(`[EnhancedEmailModule] Error fetching folders for cross-client mailbox ${mb.mailbox_id}:`, folderError);
            return { mailboxId: mb.mailbox_id, folders: [] };
          }
        })
      );

      if (cancelled) return;

      setSharedMailboxFolders((prev) => {
        const next = { ...prev };
        for (const entry of folderResults) {
          // Only set if not already populated (delegated accounts effect may have set it)
          if (!next[entry.mailboxId]) {
            next[entry.mailboxId] = entry.folders;
          }
        }
        return next;
      });
    };

    void fetchCrossClientFolders();

    return () => {
      cancelled = true;
    };
  }, [accessibleCrossClientMailboxes]);

  // Fetch delegates for active shared mailbox (for read status indicators)
  useEffect(() => {
    if (!activeSharedMailboxId) {
      setDelegates([]);
      return;
    }
    
    const fetchDelegates = async () => {
      try {
        const result = await emailApiService.getMailboxDelegates(activeSharedMailboxId);
        if (result.success && result.delegates) {
          setDelegates(result.delegates);
        }
      } catch (error) {
        if (!isSilentFetchError(error)) console.error('[EnhancedEmailModule] Failed to fetch mailbox delegates:', error);
      }
    };
    
    fetchDelegates();
  }, [activeSharedMailboxId]);

  // Track if component has been initialized to prevent duplicate WebSocket connections
  const hasInitializedRef = useRef(false);
  
  // Check for email account on mount and set up auto-sync
  useEffect(() => {
    // Prevent re-initialization on re-renders
    if (hasInitializedRef.current) return;
    
    const init = async () => {
      hasInitializedRef.current = true;

      if (!manageStoreLifecycle) {
        setEmailInitComplete(true);
        return;
      }
      
      // FIRST: Hydrate memory cache from IndexedDB for instant folder switching
      // This runs before API calls so cached data is available immediately
      const hydrateFromIndexedDB = useEmailStore.getState().hydrateFromIndexedDB;
      await hydrateFromIndexedDB();
      
      const hasAccount = await checkEmailAccount();
      
      // ALWAYS fetch delegated/shared mailboxes - even for virtual users without personal accounts
      // This ensures virtual mailbox users can access shared mailboxes and have their
      // read status tracked in read_by_users for delegate avatar indicators
      // Skip the API call when accounts are already provided via props (e.g. from EmailMobileModal)
      if (!delegatedAccountsProp || delegatedAccountsProp.length === 0) {
        await fetchDelegatedAccounts();
      }
      
      // Mark initialization complete (prevent flash of "no account" screen for virtual users)
      setEmailInitComplete(true);
      
      if (hasAccount) {
        fetchFolders();
        // Always fetch threads - all emails are stored as threads in MongoDB
        fetchThreads({ folder: 'INBOX' });
        // Connect to WebSocket for real-time sync
        connectWebSocket();
        // Smart-sync on open to catch up on emails that arrived while the
        // modal was closed (WebSocket was disconnected).  This compares
        // local thread counts against server stats and only background-
        // refreshes if there are differences, so it's lightweight when
        // nothing changed.
        smartSyncThreads('INBOX');
      }
    };
    init();
    
    // Cleanup: disconnect WebSocket on unmount only if this component owns the lifecycle
    return () => {
      if (manageStoreLifecycle) {
        disconnectWebSocket();
      }
      hasInitializedRef.current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // When delegated accounts are provided via props (e.g. from EmailMobileModal),
  // initialize store state once they arrive — avoids a duplicate API call.
  useEffect(() => {
    if (!delegatedAccountsProp || delegatedAccountsProp.length === 0) return;
    if (delegatedStoreInitRef.current) return;
    delegatedStoreInitRef.current = true;
    void fetchDelegatedAccounts(delegatedAccountsProp);
  }, [delegatedAccountsProp, fetchDelegatedAccounts]);

  useEffect(() => {
    if (!emailInitComplete || hasCheckedPendingEmailOnInitRef.current) {
      return;
    }

    hasCheckedPendingEmailOnInitRef.current = true;

    if (typeof window === 'undefined' || typeof sessionStorage === 'undefined') {
      return;
    }

    try {
      const pendingEmailJson = sessionStorage.getItem('pending_email_open');
      if (!pendingEmailJson) {
        return;
      }

      const pendingEmail = normalizePendingEmailTarget(JSON.parse(pendingEmailJson));
      const age = Date.now() - (pendingEmail?.timestamp || 0);

      if (pendingEmail && age < 60000) {
        console.log('[Email] 📧 Found pending email to open after init:', pendingEmail.emailId, 'accountId:', pendingEmail.accountId);
        void openPendingEmailTarget(pendingEmail);
      } else {
        sessionStorage.removeItem('pending_email_open');
      }
    } catch (error) {
      console.error('[Email] Error checking pending email after init:', error);
    }
  }, [emailInitComplete, openPendingEmailTarget]);

  // Push-first sync strategy:
  // - When WebSocket is connected, rely on real-time events for new mail/thread changes.
  // - If WebSocket drops, fall back to a lightweight periodic sync.
  useEffect(() => {
    if (!emailInitComplete || (!hasEmailAccount && !activeSharedMailboxId)) {
      return;
    }

    if (wsConnected) {
      return;
    }

    const fallbackInterval = setInterval(() => {
      const state = useEmailStore.getState();

      // Avoid clobbering an active server-side search.
      if (state.threadsSearchQuery) return;

      // Skip background polling for hidden tabs; we'll reconcile on visibility restore.
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') {
        return;
      }

      console.log('[EnhancedEmailModule] WebSocket offline - running fallback thread sync');
      smartSyncThreads(state.selectedFolder || 'INBOX');
    }, 5 * 60 * 1000);

    return () => clearInterval(fallbackInterval);
  }, [activeSharedMailboxId, emailInitComplete, hasEmailAccount, smartSyncThreads, wsConnected]);

  // When WebSocket comes back after being offline, do one catch-up sync.
  useEffect(() => {
    const previous = previousWsConnectedRef.current;
    previousWsConnectedRef.current = wsConnected;

    if (!emailInitComplete) {
      return;
    }

    if (previous === false && wsConnected) {
      const state = useEmailStore.getState();
      if (state.threadsSearchQuery) {
        return;
      }

      console.log('[EnhancedEmailModule] WebSocket reconnected - reconciling threads once');
      smartSyncThreads(state.selectedFolder || 'INBOX');
    }
  }, [emailInitComplete, smartSyncThreads, wsConnected]);

  // Browser/tab wake-up fallback for desktop web when WebSocket was disconnected while hidden.
  useEffect(() => {
    if (typeof document === 'undefined') {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible' || wsConnected) {
        return;
      }

      const state = useEmailStore.getState();
      if (state.threadsSearchQuery) {
        return;
      }

      console.log('[EnhancedEmailModule] Tab visible while WebSocket offline - running fallback sync');
      smartSyncThreads(state.selectedFolder || 'INBOX');
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [smartSyncThreads, wsConnected]);

  // Auto-select first shared mailbox for virtual users (no personal email account)
  // This ensures virtual mailbox users can see and interact with shared mailbox emails
  useEffect(() => {
    if (!manageStoreLifecycle) {
      return;
    }

    if (!hasEmailAccount && delegatedAccounts.length > 0 && !activeSharedMailboxId) {
      if (combinedInboxEnabled && delegatedAccounts.length > 1) {
        console.log('[EnhancedEmailModule] Shared-only user - keeping combined shared inbox active');
        return;
      }

      console.log('[EnhancedEmailModule] Virtual user detected - auto-selecting first shared mailbox');
      const firstMailbox = delegatedAccounts[0];
      setIncludeSharedMailboxThreads(false);
      setActiveSharedMailbox(firstMailbox.mailboxId);
      // Fetch threads for the auto-selected shared mailbox using accountId option
      // (setActiveSharedMailbox state may not be committed yet in Zustand)
      fetchThreads({ folder: 'INBOX', accountId: firstMailbox.mailboxId });
      // Connect WebSocket for real-time updates
      connectWebSocket();
    }
  }, [manageStoreLifecycle, hasEmailAccount, delegatedAccounts, activeSharedMailboxId, setActiveSharedMailbox, setIncludeSharedMailboxThreads, fetchThreads, connectWebSocket, combinedInboxEnabled]);

  // Mobile: Listen for app resume to sync emails
  // When user returns from background, trigger a sync
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    let resumeListener: PluginListenerHandle | undefined;
    let isDisposed = false;
    
    const setupResumeListener = async () => {
      resumeListener = await App.addListener('appStateChange', async ({ isActive }) => {
        if (isActive) {
          if (isBiometricLockActive()) {
            console.log('[EnhancedEmailModule] Resume sync deferred until biometric unlock');
            const unlocked = await waitForBiometricUnlock();
            if (!unlocked || isDisposed) {
              return;
            }
          }

          console.log('[EnhancedEmailModule] App resumed - triggering sync');
          
          // Trigger MasterSync for unified sync across all modules
          if (masterSyncOnline) {
            triggerMasterSync();
          }
          
          // Refresh threads - preserve active search query so results aren't clobbered
          const resumeSearch = useEmailStore.getState().threadsSearchQuery;
          fetchThreads({ folder: selectedFolder || 'INBOX', ...(resumeSearch ? { search: resumeSearch, forceRefresh: true } : {}) });
        }
      });
    };
    
    setupResumeListener();
    
    return () => {
      isDisposed = true;
      if (resumeListener) {
        resumeListener.remove();
      }
    };
  }, [masterSyncOnline, triggerMasterSync, fetchThreads, selectedFolder]);
  
  // Listen for FCM foreground new_email push — trigger immediate sync
  // On Capacitor the WebSocket event may arrive late or be lost after app sleep,
  // so we use the FCM push as a reliable signal to refresh the thread list.
  useEffect(() => {
    const handleNewEmailPush = () => {
      console.log('[EnhancedEmailModule] 📧 FCM new_email received — refreshing threads');
      // Small delay to allow the backend to finish indexing the email in MongoDB
      setTimeout(() => {
        smartSyncThreads(selectedFolder || 'INBOX');
      }, 1500);
    };

    window.addEventListener('emailNewEmailReceived', handleNewEmailPush);
    return () => window.removeEventListener('emailNewEmailReceived', handleNewEmailPush);
  }, [smartSyncThreads, selectedFolder]);

  // Listen for global pull-to-refresh event (from CapacitorPullToRefresh)
  useEffect(() => {
    const handlePullToRefresh = () => {
      smartSyncThreads(selectedFolder || 'INBOX');
    };
    window.addEventListener('pull-to-refresh', handlePullToRefresh);
    return () => window.removeEventListener('pull-to-refresh', handlePullToRefresh);
  }, [smartSyncThreads, selectedFolder]);

  // Listen for deepLinkSyncConnect event (triggered after external app operations)
  useEffect(() => {
    if (!Capacitor.isNativePlatform()) return;
    
    const handleSyncConnect = async (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (detail?.module !== 'email') return; // Only handle email sync
      
      console.log('[EnhancedEmailModule] 🔄 Received syncConnect deep link:', detail);
      
      // Trigger MasterSync for unified sync
      if (masterSyncOnline) {
        await triggerMasterSync();
      }
      
      // Refresh threads - preserve active search query so results aren't clobbered
      const deepLinkSearch = useEmailStore.getState().threadsSearchQuery;
      fetchThreads({ folder: selectedFolder || 'INBOX', ...(deepLinkSearch ? { search: deepLinkSearch, forceRefresh: true } : {}) });
      
      notify.success('Email synced');
    };
    
    // Listen for both event names for compatibility
    window.addEventListener('syncConnect', handleSyncConnect);
    window.addEventListener('deepLinkSyncConnect', handleSyncConnect);
    
    return () => {
      window.removeEventListener('syncConnect', handleSyncConnect);
      window.removeEventListener('deepLinkSyncConnect', handleSyncConnect);
    };
  }, [masterSyncOnline, triggerMasterSync, fetchThreads, selectedFolder]);

  // Handle initial email ID from cold start (passed from EmailModal)
  // Shared-mailbox-only users may have no personal account but still use email via activeSharedMailboxId
  useEffect(() => {
    let pendingTarget: PendingEmailOpenContext | null = null;

    if (typeof window !== 'undefined' && typeof sessionStorage !== 'undefined') {
      try {
        const pendingEmailJson = sessionStorage.getItem('pending_email_open');
        if (pendingEmailJson) {
          pendingTarget = normalizePendingEmailTarget(JSON.parse(pendingEmailJson));
        }
      } catch (error) {
        console.error('[Email] Failed to parse pending email from session storage:', error);
      }
    }

    if (!pendingTarget && initialEmailId) {
      pendingTarget = {
        emailId: initialEmailId,
        folder: selectedFolder || 'INBOX',
        accountId: activeSharedMailboxId || undefined,
      };
    }

    if (!pendingTarget) return;
    void openPendingEmailTarget(pendingTarget);
  }, [activeSharedMailboxId, initialEmailId, openPendingEmailTarget, selectedFolder]);

  // Click-outside handler for mobile dropdowns
  useEffect(() => {
    if (!isModal) return;
    
    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      // Close folder dropdown if clicked outside
      if (showFolderDropdown && folderDropdownRef.current && !folderDropdownRef.current.contains(event.target as Node)) {
        setShowFolderDropdown(false);
      }
      if (showBulkMoveDropdown && bulkMoveDropdownRef.current && !bulkMoveDropdownRef.current.contains(event.target as Node)) {
        setShowBulkMoveDropdown(false);
      }
      // Close account selector if clicked outside
      if (showAccountSelector && accountSelectorRef.current && !accountSelectorRef.current.contains(event.target as Node)) {
        setShowAccountSelector(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isModal, showBulkMoveDropdown, showFolderDropdown, showAccountSelector]);

  useEffect(() => {
    if (!showSortDropdown) return;

    const handleClickOutside = (event: MouseEvent | TouchEvent) => {
      if (
        sortDropdownRef.current &&
        !sortDropdownRef.current.contains(event.target as Node)
      ) {
        setShowSortDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [showSortDropdown]);

  // Standard folders: use module-level STANDARD_EMAIL_FOLDERS for stable useMemo deps

  const currentMailboxContext = useMemo<string | null>(() => {
    if (activeSharedMailboxId) {
      return `shared:${activeSharedMailboxId}`;
    }

    if (!hasEmailAccount && shouldShowCombinedMailboxOption) {
      return 'shared-combined';
    }

    if (hasEmailAccount && shouldShowCombinedMailboxOption && nullMailboxMode === 'combined') {
      return 'combined';
    }

    if (hasEmailAccount) {
      return 'primary';
    }

    if (delegatedAccounts[0]) {
      return `shared:${delegatedAccounts[0].mailboxId}`;
    }

    return null;
  }, [activeSharedMailboxId, delegatedAccounts, hasEmailAccount, nullMailboxMode, shouldShowCombinedMailboxOption]);

  useEffect(() => {
    setIncludeSharedMailboxThreads(
      currentMailboxContext === 'combined' || currentMailboxContext === 'shared-combined'
    );
  }, [currentMailboxContext, setIncludeSharedMailboxThreads]);

  // Get list of available mailboxes for filtering
  const availableMailboxes = useMemo(() => {
    const mailboxes: MailboxOption[] = [];
    const addedEmails = new Set<string>();
    const combinedUnreadCount = inboxUnreadCount + delegatedAccounts.reduce((sum, account) => {
      return sum + (sharedMailboxUnreadCounts[account.mailboxId] || account.unreadCount || 0);
    }, 0);

    if (shouldShowCombinedMailboxOption) {
      mailboxes.push({
        contextId: hasEmailAccount ? 'combined' : 'shared-combined',
        mailboxId: null,
        email: hasEmailAccount ? 'combined-inbox' : 'shared-inboxes',
        label: 'Combined inbox',
        type: 'combined',
        unreadCount: combinedUnreadCount,
        badgeLabel: hasEmailAccount ? 'All mailboxes' : 'Shared mailboxes',
        icon: Users,
      });
    }

    // Primary account
    if (primaryAccount?.email) {
      const email = primaryAccount.email.toLowerCase();
      mailboxes.push({
        contextId: 'primary',
        mailboxId: null,
        email: primaryAccount.email,
        label: primaryAccount.email,
        type: 'primary',
        unreadCount: inboxUnreadCount,
        badgeLabel: 'Primary',
        icon: Mail,
      });
      addedEmails.add(email);
    }

    // Delegated/shared accounts
    delegatedAccounts.forEach(acc => {
      const email = acc.emailAddress.toLowerCase();
      if (!addedEmails.has(email)) {
        mailboxes.push({
          contextId: `shared:${acc.mailboxId}`,
          mailboxId: acc.mailboxId,
          email: acc.emailAddress,
          label: acc.emailAddress,
          type: 'shared',
          unreadCount: sharedMailboxUnreadCounts[acc.mailboxId] || acc.unreadCount || 0,
          isPartnerAccount: acc.isPartnerAccount,
          partnerClientName: acc.partnerClientName,
          badgeLabel: acc.isPartnerAccount
            ? (acc.partnerClientName || 'Partner')
            : acc.isTeamMailbox
              ? (acc.teamName || acc.displayName || 'Shared')
              : (acc.displayName && acc.displayName !== acc.emailAddress ? acc.displayName : 'Shared'),
          icon: acc.isPartnerAccount ? Building2 : Users,
        });
        addedEmails.add(email);
      }
    });

    // Cross-client personal mailboxes — the user's own mailboxes at other
    // clients. These are switchable via the same shared:<mailboxId> mechanism
    // (setting activeSharedMailboxId scopes thread/folder fetches to that
    // mailbox). Skipped when already represented as primary or delegated.
    accessibleCrossClientMailboxes.forEach(mb => {
      const email = (mb.email_address || '').toLowerCase();
      if (!email || addedEmails.has(email)) return;
      mailboxes.push({
        contextId: `shared:${mb.mailbox_id}`,
        mailboxId: mb.mailbox_id,
        email: mb.email_address,
        label: mb.email_address,
        type: 'personal-cross-client',
        unreadCount: sharedMailboxUnreadCounts[mb.mailbox_id] || 0,
        clientName: mb.client_name,
        clientId: mb.client_id,
        badgeLabel: mb.client_name,
        icon: Building2,
      });
      addedEmails.add(email);
    });

    return mailboxes;
  }, [accessibleCrossClientMailboxes, delegatedAccounts, hasEmailAccount, inboxUnreadCount, primaryAccount, sharedMailboxUnreadCounts, shouldShowCombinedMailboxOption]);

  const currentMailboxOption = useMemo(() => {
    if (!currentMailboxContext) {
      return availableMailboxes[0] || null;
    }

    return availableMailboxes.find((mailbox) => mailbox.contextId === currentMailboxContext) || availableMailboxes[0] || null;
  }, [availableMailboxes, currentMailboxContext]);

  const activeMailboxFolders = useMemo(() => {
    if (activeSharedMailboxId) {
      return sharedMailboxFolders[activeSharedMailboxId] || [];
    }

    return folders;
  }, [activeSharedMailboxId, folders, sharedMailboxFolders]);

  const availableFromAccounts = useMemo((): FromAccount[] => {
    const accounts: FromAccount[] = [];

    if (primaryAccount) {
      const primaryEmail = primaryAccount.default_send_as || primaryAccount.email;
      const primaryAlias = primaryAccount.all_aliases?.find((alias) => alias.email === primaryEmail);
      const connectorEmail = primaryAccount.linked_connector?.email?.toLowerCase();
      const primaryIsConnector = primaryEmail?.toLowerCase() === connectorEmail;

      if (primaryIsConnector && primaryAccount.linked_connector) {
        accounts.push({
          mailboxId: primaryAccount.id,
          emailAddress: primaryEmail,
          displayName: primaryAlias?.display_name || primaryAccount.linked_connector.name || primaryEmail,
          isShared: false,
          isPrimary: true,
          isConnector: true,
          connectorId: primaryAccount.linked_connector.id,
          hasDeliveryIssues: primaryAccount.linked_connector.has_delivery_issues,
          deliverabilityScore: primaryAccount.linked_connector.deliverability_score,
        });

        if (primaryAccount.email && primaryAccount.email !== primaryEmail) {
          accounts.push({
            mailboxId: primaryAccount.id,
            emailAddress: primaryAccount.email,
            displayName: `${primaryAccount.name || primaryAccount.email} (System)`,
            isShared: false,
            isSystemAddress: true,
          });
        }
      } else {
        accounts.push({
          mailboxId: primaryAccount.id,
          emailAddress: primaryEmail,
          displayName: primaryAlias?.display_name || primaryAccount.name || primaryEmail,
          isShared: false,
          isPrimary: true,
          isAlias: !!primaryAlias,
          aliasId: primaryAlias?.id,
        });

        if (primaryAccount.email && primaryAccount.email !== primaryEmail) {
          accounts.push({
            mailboxId: primaryAccount.id,
            emailAddress: primaryAccount.email,
            displayName: `${primaryAccount.name || primaryAccount.email} (System)`,
            isShared: false,
            isSystemAddress: true,
          });
        }

        if (primaryAccount.linked_connector) {
          accounts.push({
            mailboxId: primaryAccount.id,
            emailAddress: primaryAccount.linked_connector.email,
            displayName: primaryAccount.linked_connector.name || primaryAccount.linked_connector.email,
            isShared: false,
            isConnector: true,
            connectorId: primaryAccount.linked_connector.id,
            hasDeliveryIssues: primaryAccount.linked_connector.has_delivery_issues,
            deliverabilityScore: primaryAccount.linked_connector.deliverability_score,
          });
        }
      }

      primaryAccount.all_aliases?.forEach((alias) => {
        const aliasEmailLower = alias.email.toLowerCase();
        if (
          alias.email !== primaryEmail &&
          alias.email !== primaryAccount.email &&
          aliasEmailLower !== connectorEmail
        ) {
          accounts.push({
            mailboxId: primaryAccount.id,
            emailAddress: alias.email,
            displayName: alias.display_name || primaryAccount.name || alias.email,
            isShared: false,
            isAlias: true,
            aliasId: alias.id,
          });
        }
      });
    }

    delegatedAccounts.forEach((account) => {
      if (!(account.permissions.canSend || account.permissions.canSendAs || account.permissions.canSendOnBehalf)) {
        return;
      }

      const baseSharedFields = {
        isShared: true,
        isPartnerAccount: account.isPartnerAccount,
        partnerClientId: account.partnerClientId,
        partnerClientName: account.partnerClientName,
        isTeamMailbox: account.isTeamMailbox,
        teamId: account.teamId ? String(account.teamId) : undefined,
        teamName: account.teamName || undefined,
        teamColor: account.teamColor || undefined,
        permissions: {
          canSend: account.permissions.canSend,
          canSendAs: account.permissions.canSendAs,
          canSendOnBehalf: account.permissions.canSendOnBehalf,
        },
      } satisfies Partial<FromAccount>;

      if (account.forceConnectorSmtp && account.linkedConnector) {
        accounts.push({
          mailboxId: account.mailboxId,
          emailAddress: account.linkedConnector.email,
          displayName: account.displayName || account.linkedConnector.name,
          isConnector: true,
          connectorId: account.linkedConnector.id,
          deliverabilityScore: account.linkedConnector.deliverabilityScore,
          hasDeliveryIssues: account.linkedConnector.hasDeliveryIssues,
          ...baseSharedFields,
        });

        account.aliases?.forEach((alias) => {
          accounts.push({
            mailboxId: account.mailboxId,
            emailAddress: alias.email,
            displayName: `${account.displayName} (${alias.email})`,
            isAlias: true,
            aliasId: alias.id,
            ...baseSharedFields,
          });
        });

        return;
      }

      const primaryEmail = account.defaultSendAs || account.emailAddress;
      const isPrimaryAlias = primaryEmail !== account.emailAddress;

      accounts.push({
        mailboxId: account.mailboxId,
        emailAddress: primaryEmail,
        displayName: account.displayName,
        isPrimary: true,
        isAlias: isPrimaryAlias,
        ...baseSharedFields,
      });

      if (isPrimaryAlias) {
        accounts.push({
          mailboxId: account.mailboxId,
          emailAddress: account.emailAddress,
          displayName: `${account.displayName} (System)`,
          isSystemAddress: true,
          ...baseSharedFields,
        });
      }

      if (account.linkedConnector) {
        accounts.push({
          mailboxId: account.mailboxId,
          emailAddress: account.linkedConnector.email,
          displayName: `${account.displayName} via ${account.linkedConnector.name}`,
          isConnector: true,
          connectorId: account.linkedConnector.id,
          deliverabilityScore: account.linkedConnector.deliverabilityScore,
          hasDeliveryIssues: account.linkedConnector.hasDeliveryIssues,
          ...baseSharedFields,
        });
      }

      account.aliases?.forEach((alias) => {
        if (alias.email !== primaryEmail) {
          accounts.push({
            mailboxId: account.mailboxId,
            emailAddress: alias.email,
            displayName: `${account.displayName} (${alias.email})`,
            isAlias: true,
            aliasId: alias.id,
            ...baseSharedFields,
          });
        }
      });
    });

    if (primaryAccount?.default_send_as) {
      const defaultAddress = primaryAccount.default_send_as;
      accounts.sort((left, right) => {
        if (left.emailAddress === defaultAddress) return -1;
        if (right.emailAddress === defaultAddress) return 1;
        return 0;
      });
    }

    // Cross-client personal mailboxes — add as sendable "from" accounts so the
    // user can compose from any of their mailboxes across clients.
    const existingFromEmails = new Set(accounts.map((a) => a.emailAddress.toLowerCase()));
    for (const mb of accessibleCrossClientMailboxes) {
      const email = (mb.email_address || '').toLowerCase();
      if (!email || existingFromEmails.has(email)) continue;
      accounts.push({
        mailboxId: mb.mailbox_id,
        emailAddress: mb.email_address,
        displayName: mb.display_name || mb.email_address,
        isShared: false,
        isPrimary: false,
        isCrossClientPersonal: true,
        partnerClientId: mb.client_id,
        partnerClientName: mb.client_name,
      });
      existingFromEmails.add(email);
    }

    return accounts;
  }, [accessibleCrossClientMailboxes, delegatedAccounts, primaryAccount]);

  const getContextualFromAccount = useCallback((): FromAccount | null => {
    if (activeSharedMailboxId) {
      return (
        availableFromAccounts.find((account) => account.mailboxId === activeSharedMailboxId && account.isPrimary) ||
        availableFromAccounts.find((account) => account.mailboxId === activeSharedMailboxId && account.isConnector) ||
        availableFromAccounts.find((account) => account.mailboxId === activeSharedMailboxId) ||
        null
      );
    }

    if (!primaryAccount && availableFromAccounts.length > 0) {
      return availableFromAccounts[0];
    }

    return null;
  }, [activeSharedMailboxId, availableFromAccounts, primaryAccount]);

  // Combine system and custom folders for display
  const displayFolders = useMemo<Array<{
    name: string;
    label: string;
    icon: typeof FolderOpen;
    isSystem: boolean;
    isCustom: boolean;
    color?: string;
    mailboxContext?: string | null;
  }>>(() => {
    const systemFolders = STANDARD_EMAIL_FOLDERS.map((sf) => ({
      ...sf,
      isSystem: true,
      isCustom: false,
      color: undefined,
      mailboxContext: currentMailboxContext,
    }));

    const customFolders = activeMailboxFolders
      .filter((f) => !f.isSystem && f.type === 'custom')
      .map((cf) => ({
        name: cf.name,
        label: cf.displayName || cf.name,
        icon: FolderOpen,
        isSystem: false,
        isCustom: true,
        color: cf.color,
        mailboxContext: currentMailboxContext,
      }));

    const merged = [...systemFolders, ...customFolders];
    const seenKeys = new Set(
      merged.map((f) => normalizeFolderKey(f.name)),
    );

    for (const apiFolder of activeMailboxFolders) {
      const rawName = String(apiFolder.path || apiFolder.name || '').trim();
      if (!rawName) continue;
      const key = normalizeFolderKey(rawName);
      if (!key || key === 'inbox') continue;
      if (seenKeys.has(key)) continue;
      seenKeys.add(key);
      merged.push({
        name: rawName,
        label: apiFolder.displayName || apiFolder.name || rawName,
        icon: FolderOpen,
        isSystem: !!apiFolder.isSystem,
        isCustom: apiFolder.type === 'custom' || !!apiFolder.isCustom,
        color: apiFolder.color,
        mailboxContext: currentMailboxContext,
      });
    }

    return merged;
  }, [activeMailboxFolders, currentMailboxContext]);

  const threadMoveFolders = useMemo(
    () => displayFolders.map((folder) => ({
      name: folder.name,
      label: folder.label,
      color: folder.color,
      isCustom: folder.isCustom,
    })),
    [displayFolders]
  );

  const bulkMoveFolders = useMemo(() => {
    const currentFolder = normalizeFolderKey(selectedFolder);
    const seen = new Set<string>();

    return threadMoveFolders.filter((folder) => {
      const normalizedName = normalizeFolderKey(folder.name);
      if (!normalizedName || normalizedName === currentFolder) return false;
      if (normalizedName === 'outbox' || normalizedName === 'scheduled') return false;
      if (seen.has(normalizedName)) return false;
      seen.add(normalizedName);
      return true;
    });
  }, [selectedFolder, threadMoveFolders]);

  const selectedFolderDisplay = useMemo(() => {
    const fromDisplay = displayFolders.find(
      (f) => f.name === selectedFolder || f.label === selectedFolder,
    );
    if (fromDisplay) {
      return { label: fromDisplay.label, icon: fromDisplay.icon };
    }
    const fromStandard = STANDARD_EMAIL_FOLDERS.find(
      (f) => f.name === selectedFolder || f.label === selectedFolder,
    );
    if (fromStandard) {
      return { label: fromStandard.label, icon: fromStandard.icon };
    }
    return { label: selectedFolder, icon: Inbox };
  }, [displayFolders, selectedFolder]);

  // Sort threads based on sort state
  const sortedThreads = useMemo(() => {
    if (!threads || threads.length === 0) return threads;

    const isSentFolder = String(selectedFolder || '').trim().toLowerCase() === 'sent';
    const getThreadSortTimestamp = (thread: ThreadListItem) => {
      const timestamp = isSentFolder
        ? thread.last_sent_at || thread.last_message_at || thread.messages?.[0]?.date
        : thread.last_message_at || thread.messages?.[0]?.date;

      return new Date(timestamp || 0).getTime();
    };
    
    return [...threads].sort((a, b) => {
      let comparison = 0;
      
      switch (sortBy) {
        case 'date':
          const dateA = getThreadSortTimestamp(a);
          const dateB = getThreadSortTimestamp(b);
          // For desc (newest first), we want higher dates first, so dateA - dateB
          comparison = dateA - dateB;
          break;
        case 'subject':
          comparison = (a.subject || '').localeCompare(b.subject || '');
          break;
        case 'from':
          const fromA = a.participants?.[0]?.name || a.participants?.[0]?.email || '';
          const fromB = b.participants?.[0]?.name || b.participants?.[0]?.email || '';
          comparison = fromA.localeCompare(fromB);
          break;
        default:
          comparison = 0;
      }
      
      // desc = newest/Z first (negate comparison), asc = oldest/A first
      return sortOrder === 'desc' ? -comparison : comparison;
    });
  }, [selectedFolder, sortBy, sortOrder, threads]);

  // Apply unread filter, optional instant local filter while MongoDB search catches up
  const displayThreads = useMemo(() => {
    let filtered = sortedThreads;

    if (showUnreadOnly) {
      filtered = filtered.filter((t) => t.has_unread);
    }

    const localQ = searchQuery.trim().toLowerCase();
    const serverQ = threadsSearchQuery.trim().toLowerCase();
    if (localQ.length >= 2 && localQ !== serverQ) {
      filtered = filtered.filter((t) => threadMatchesQuickSearch(t, localQ));
    }

    return filtered;
  }, [sortedThreads, showUnreadOnly, searchQuery, threadsSearchQuery]);

  const selectedThreadIdSet = useMemo(() => new Set(selectedThreadIds), [selectedThreadIds]);

  const selectedThreads = useMemo(
    () => displayThreads.filter((thread) => selectedThreadIdSet.has(thread.thread_id)),
    [displayThreads, selectedThreadIdSet]
  );

  const clearMultiSelection = useCallback(() => {
    setSelectedThreadIds([]);
    setMultiSelectMode(false);
  }, []);

  const handleToggleThreadSelection = useCallback((thread: ThreadListItem, options?: { enterMultiSelect?: boolean }) => {
    if (options?.enterMultiSelect) {
      setMultiSelectMode(true);
    }

    setSelectedThreadIds((prev) => (
      prev.includes(thread.thread_id)
        ? prev.filter((threadId) => threadId !== thread.thread_id)
        : [...prev, thread.thread_id]
    ));
  }, []);

  const handleOpenThreadFromList = useCallback((thread: ThreadListItem) => {
    clearMultiSelection();
    selectThread(thread);
    void haptics.light();

    if (isModal) {
      setMobileView('email');
    }

    onThreadSelected?.(thread.thread_id);
  }, [clearMultiSelection, isModal, onThreadSelected, selectThread]);

  useEffect(() => {
    const availableThreadIds = new Set(displayThreads.map((thread) => thread.thread_id));

    setSelectedThreadIds((prev) => {
      const next = prev.filter((threadId) => availableThreadIds.has(threadId));
      return next.length === prev.length ? prev : next;
    });
  }, [displayThreads]);

  useEffect(() => {
    if (multiSelectMode && selectedThreadIds.length === 0) {
      setMultiSelectMode(false);
    }
  }, [multiSelectMode, selectedThreadIds.length]);

  useEffect(() => {
    if (selectedThreadIds.length === 0) {
      setShowBulkMoveDropdown(false);
    }
  }, [selectedThreadIds.length]);

  useEffect(() => {
    if (clearMultiSelectSignal === undefined) {
      return;
    }

    if (clearMultiSelectSignalRef.current === clearMultiSelectSignal) {
      return;
    }

    clearMultiSelectSignalRef.current = clearMultiSelectSignal;
    clearMultiSelection();
  }, [clearMultiSelectSignal, clearMultiSelection]);
  
  // Check if we're in search mode (server-side or typing ahead of debounced server search)
  const isSearching =
    (threadsSearchQuery && threadsSearchQuery.trim().length >= 2) ||
    (searchQuery.trim().length >= 2 &&
      searchQuery.trim().toLowerCase() !== threadsSearchQuery.trim().toLowerCase());
  const emailRealtimeStatus: EmailRealtimeStatus = wsConnected
    ? 'live'
    : masterSyncOnline
      ? 'online'
      : 'offline';
  const emailRealtimeLabel = emailRealtimeStatus === 'live'
    ? 'Live'
    : emailRealtimeStatus === 'online'
      ? 'Online'
      : 'Offline';

  // Report status info to parent (for modal header display)
  useEffect(() => {
    onStatusInfo?.({
      threadCount: displayThreads.length,
      wsConnected,
      isOnline: masterSyncOnline,
      realtimeStatus: emailRealtimeStatus,
      isSearching: !!isSearching,
      selectedCount: selectedThreadIds.length,
      isSelecting: multiSelectMode || selectedThreadIds.length > 0,
    });
  }, [displayThreads.length, emailRealtimeStatus, isSearching, masterSyncOnline, multiSelectMode, onStatusInfo, selectedThreadIds.length, wsConnected]);

  // Handle folder selection (with threads mode support and per-mailbox inbox)
  const handleSelectFolder = (folderName: string, mailboxId?: string | null) => {
    void haptics.selection();
    const effectiveMailboxContext = mailboxId === undefined ? currentMailboxContext : mailboxId;
    console.log('[EnhancedEmailModule] handleSelectFolder called:', { folderName, mailboxId: effectiveMailboxContext });

    let accountIdForFetch: number | undefined;

    // Set active shared mailbox based on mailboxId BEFORE calling selectFolder
    // so that selectFolder computes the correct cache key.
    // mailboxId format: "shared:95" for shared, "primary" for personal, null for all
    if (effectiveMailboxContext && typeof effectiveMailboxContext === 'string' && effectiveMailboxContext.startsWith('shared:')) {
      const sharedId = parseInt(effectiveMailboxContext.replace('shared:', ''), 10);
      if (!isNaN(sharedId)) {
        setNullMailboxMode('primary');
        setIncludeSharedMailboxThreads(false);
        setActiveSharedMailbox(sharedId);
        accountIdForFetch = sharedId;
      }
    } else if (
      effectiveMailboxContext === 'combined' ||
      effectiveMailboxContext === 'primary' ||
      effectiveMailboxContext === 'shared-combined' ||
      effectiveMailboxContext === null
    ) {
      if (effectiveMailboxContext === 'combined' || effectiveMailboxContext === 'shared-combined') {
        setNullMailboxMode('combined');
        setIncludeSharedMailboxThreads(true);
      } else if (effectiveMailboxContext === 'primary') {
        setNullMailboxMode('primary');
        setIncludeSharedMailboxThreads(false);
      }

      // Combined inbox or personal mailbox — clear shared mailbox filter
      if (
        effectiveMailboxContext === null ||
        effectiveMailboxContext === 'combined' ||
        effectiveMailboxContext === 'shared-combined' ||
        hasEmailAccount
      ) {
        setActiveSharedMailbox(null);
      }
    }

    // Now call selectFolder — it will use the updated activeSharedMailboxId
    // and includeSharedMailboxThreads to compute the correct cache key.
    selectFolder(folderName);
    selectThread(null);

    // Notify parent of folder change if callback provided
    if (onFolderChange) {
      onFolderChange(folderName);
    }

    // Reset mobile view to list when changing folders
    if (isModal) {
      setMobileView('list');
    }

    // For Outbox/Scheduled folders, fetch queue items instead of threads
    const isOutboxFolder = folderName.toLowerCase() === 'outbox' || folderName.toLowerCase() === 'scheduled';
    if (isOutboxFolder) {
      console.log('[EnhancedEmailModule] Fetching outbox for folder:', folderName);
      fetchOutbox(accountIdForFetch ?? null);
    } else {
      console.log('[EnhancedEmailModule] Calling fetchThreads with folder:', folderName);
      fetchThreads({
        folder: folderName,
        accountId: accountIdForFetch,
        includeSharedMailboxThreads: effectiveMailboxContext === 'combined' || effectiveMailboxContext === 'shared-combined',
      });
    }
  };

  // Sync with external folder prop when it changes
  useEffect(() => {
    if (!externalFolder || !hasEmailAccount) return;

    // Only update if different from current folder
    if (externalFolder !== selectedFolder) {
      console.log('[EnhancedEmailModule] Syncing with external folder:', externalFolder);
      handleSelectFolder(externalFolder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- omit non-memoized handler to avoid re-running every render
  }, [externalFolder, hasEmailAccount, selectedFolder]);

  const linkedConnector = primaryAccount?.linked_connector;
  const shouldSyncLinkedConnector = Boolean(linkedConnector?.id && !activeSharedMailboxId);
  const syncButtonTitle = shouldSyncLinkedConnector ? 'Sync external account and refresh' : 'Refresh';

  const syncLinkedConnector = useCallback(async () => {
    if (!linkedConnector?.id) {
      return null;
    }

    const tokens = authService.getTokens();
    if (!tokens?.access_token) {
      throw new Error('Authentication required to sync the linked email account');
    }

    const response = await fetch('/api/v1/email-domains', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${tokens.access_token}`,
      },
      body: JSON.stringify({
        action: 'syncConnector',
        connector_id: linkedConnector.id,
      }),
    });

    const result = await response.json();

    if (!response.ok || !result?.success) {
      throw new Error(result?.error || 'Failed to sync linked external mailbox');
    }

    return result;
  }, [linkedConnector?.id]);

  const refreshOutbox = useCallback(async () => {
    const sharedMailboxId = activeSharedMailboxId || null;

    try {
      const processResult = await emailApiService.processOutbox(20);
      if (!processResult.success) {
        throw new Error(processResult.error || 'Failed to process queued outbox emails');
      }

      console.log('[EnhancedEmailModule] Outbox process result:', processResult);
    } finally {
      await fetchOutbox(sharedMailboxId);
    }
  }, [activeSharedMailboxId, fetchOutbox]);

  // Handle sync - use smart sync to only refresh if there are changes
  const handleSync = useCallback(async () => {
    try {
      const isOutboxFolder = selectedFolder.toLowerCase() === 'outbox' || selectedFolder.toLowerCase() === 'scheduled';
      if (isOutboxFolder) {
        await refreshOutbox();

        setShowSyncSuccess(true);
        setTimeout(() => setShowSyncSuccess(false), 3000);
        return;
      }

      if (shouldSyncLinkedConnector) {
        const connectorSyncResult = await syncLinkedConnector();
        console.log('[EnhancedEmailModule] Linked connector sync result:', connectorSyncResult);
      }

      const result = await smartSyncThreads(selectedFolder);
      console.log('[EnhancedEmailModule] Smart sync result:', result);

      if (result.reason === 'error') {
        notify.error('Failed to refresh emails');
        return;
      }

      // Show success checkmark for 3 seconds
      setShowSyncSuccess(true);
      setTimeout(() => setShowSyncSuccess(false), 3000);
    } catch (error: unknown) {
      console.error('[EnhancedEmailModule] Sync failed:', error);
      notify.error(error instanceof Error ? error.message : 'Failed to sync emails');
    }
  }, [refreshOutbox, selectedFolder, shouldSyncLinkedConnector, smartSyncThreads, syncLinkedConnector]);

  // Handle search form submit - triggers server-side search
  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      setThreadsSearchQuery(searchQuery.trim());
    }
  };

  // Handle clearing search
  const handleClearSearch = () => {
    setSearchQuery('');
    setThreadsSearchQuery(''); // Clear server-side search
  };

  // Track previous search query to detect when it's cleared
  const prevSearchQueryRef = useRef(searchQuery);

  // Effect to trigger server search when query changes
  useEffect(() => {
    const wasSearching = prevSearchQueryRef.current.length >= 2;
    const isNowSearching = searchQuery.length >= 2;
    prevSearchQueryRef.current = searchQuery;
    
    // Trigger server search after typing stops (debounced)
    if (isNowSearching) {
      const timer = setTimeout(() => {
        setThreadsSearchQuery(searchQuery);
      }, 450);
      return () => clearTimeout(timer);
    } else if (wasSearching && !isNowSearching) {
      // Search was cleared
      setThreadsSearchQuery(''); // Trigger refresh without search
    }
  }, [searchQuery, setThreadsSearchQuery]);

  // Handle compose
  const handleCompose = useCallback(() => {
    void haptics.medium();
    if (onExternalCompose) {
      onExternalCompose();
      return;
    }
    setReplyTo(null);
    setPendingComposerFromAccount(getContextualFromAccount());
    setShowComposer(true);
  }, [getContextualFromAccount, onExternalCompose]);

  const handleSyncRef = useRef(handleSync);
  const handleComposeRef = useRef(handleCompose);
  const handleSelectFolderRef = useRef(handleSelectFolder);
  handleSyncRef.current = handleSync;
  handleComposeRef.current = handleCompose;
  handleSelectFolderRef.current = handleSelectFolder;

  const handleModalSortChange = useCallback((value: string) => {
    const next = value as 'date' | 'subject' | 'from';
    if (next === sortBy) {
      setSortOrder((prev) => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setSortBy(next);
      setSortOrder('desc');
    }
  }, [sortBy, setSortBy, setSortOrder]);

  useEffect(() => {
    if (!isModal || !onModalChromeReady) {
      return;
    }
    onModalChromeReady({
      sync: () => handleSyncRef.current(),
      openSettings: () => setShowSettings(true),
      compose: () => handleComposeRef.current(),
      listView: resolvedMobileView === 'list',
      search: {
        value: searchQuery,
        onChange: (v: string) => setSearchQuery(v),
      },
      filters: {
        options: [
          ...availableMailboxes.map((mb) => ({
            value: `mb:${mb.contextId}`,
            label: mb.label,
          })),
          ...displayFolders
            .filter((f) => f.name !== 'INBOX')
            .map((f) => ({
              value: `f:${f.name}`,
              label: f.label,
            })),
        ],
        value:
          selectedFolder === 'INBOX'
            ? `mb:${currentMailboxContext ?? 'primary'}`
            : `f:${selectedFolder}`,
        onChange: (v: string) => {
          if (v.startsWith('mb:')) {
            handleSelectFolderRef.current('INBOX', v.slice(3));
          } else if (v.startsWith('f:')) {
            const name = v.slice(2);
            const folder = displayFolders.find((f) => f.name === name);
            handleSelectFolderRef.current(
              name,
              folder?.mailboxContext ?? currentMailboxContext,
            );
          }
        },
        dropdownLabel: 'Folders & mailboxes',
      },
      sort: {
        options: [
          { value: 'date', label: 'Date' },
          { value: 'subject', label: 'Subject' },
          { value: 'from', label: 'From' },
        ],
        value: sortBy,
        onChange: handleModalSortChange,
      },
    });
    return () => onModalChromeReady(null);
  }, [
    isModal,
    onModalChromeReady,
    resolvedMobileView,
    searchQuery,
    availableMailboxes,
    displayFolders,
    selectedFolder,
    currentMailboxContext,
    sortBy,
    handleModalSortChange,
  ]);

  // Handle reply (supports both Email and ThreadMessage)
  const handleReply = (email: Email, all: boolean = false) => {
    if (onExternalReply) {
      onExternalReply(email, all);
      return;
    }
    setReplyTo({ email, all });
    setShowComposer(true);
  };

  // Handle reply from thread message
  const handleThreadReply = (message?: ThreadMessage) => {
    if (message) {
      // Convert ThreadMessage to Email-like format for composer
      // Normalize from/to/cc to have both email and address properties
      // Include full thread chain below the current message
      const threadMessages = selectedThread?.messages || [];
      const chainHtml = buildThreadChainHtml(threadMessages, message.message_id);
      const msgHtml = message.html_content || message.body_html || '';
      const msgText = message.text_content || message.body_text || '';
        const emailLike = buildComposerEmailFromMessage(
          message,
          selectedThread,
          (msgHtml || msgText || '') + chainHtml,
          msgText
        );
      if (onExternalReply) {
        onExternalReply(emailLike, false);
        return;
      }
      setReplyTo({ email: emailLike, all: false });
    } else if (selectedThread) {
      // Reply to last message in thread
      const messages = selectedThread.messages || [];
      const lastMessage = messages[messages.length - 1];
      if (lastMessage) {
        const chainHtml = buildThreadChainHtml(messages, lastMessage.message_id);
        const lastMsgHtml = lastMessage.html_content || lastMessage.body_html || '';
        const lastMsgText = lastMessage.text_content || lastMessage.body_text || '';
        const emailLike = buildComposerEmailFromMessage(
          lastMessage,
          selectedThread,
          (lastMsgHtml || lastMsgText || '') + chainHtml,
          lastMsgText
        );
        if (onExternalReply) {
          onExternalReply(emailLike, false);
          return;
        }
        setReplyTo({ email: emailLike, all: false });
      }
    }
    setPendingComposerFromAccount(getContextualFromAccount());
    setShowComposer(true);
  };

  const handleThreadReplyAll = (message?: ThreadMessage) => {
    if (message) {
      const threadMessages = selectedThread?.messages || [];
      const chainHtml = buildThreadChainHtml(threadMessages, message.message_id);
      const raMsgHtml = message.html_content || message.body_html || '';
      const raMsgText = message.text_content || message.body_text || '';
      const emailLike = buildComposerEmailFromMessage(
        message,
        selectedThread,
        (raMsgHtml || raMsgText || '') + chainHtml,
        raMsgText
      );
      if (onExternalReply) {
        onExternalReply(emailLike, true);
        return;
      }
      setReplyTo({ email: emailLike, all: true });
    } else if (selectedThread) {
      const messages = selectedThread.messages || [];
      const lastMessage = messages[messages.length - 1];
      if (lastMessage) {
        const chainHtml = buildThreadChainHtml(messages, lastMessage.message_id);
        const raLastMsgHtml = lastMessage.html_content || lastMessage.body_html || '';
        const raLastMsgText = lastMessage.text_content || lastMessage.body_text || '';
        const emailLike = buildComposerEmailFromMessage(
          lastMessage,
          selectedThread,
          (raLastMsgHtml || raLastMsgText || '') + chainHtml,
          raLastMsgText
        );
        if (onExternalReply) {
          onExternalReply(emailLike, true);
          return;
        }
        setReplyTo({ email: emailLike, all: true });
      }
    }
    setPendingComposerFromAccount(getContextualFromAccount());
    setShowComposer(true);
  };

  const handleThreadForward = (message?: ThreadMessage) => {
    if (message) {
      const threadMessages = selectedThread?.messages || [];
      const chainHtml = buildThreadChainHtml(threadMessages, message.message_id);
      const fwdMsgHtml = message.html_content || message.body_html || '';
      const fwdMsgText = message.text_content || message.body_text || '';
      const emailLike = buildComposerEmailFromMessage(
        message,
        selectedThread,
        (fwdMsgHtml || fwdMsgText || '') + chainHtml,
        fwdMsgText
      );
      if (onExternalForward) {
        onExternalForward(emailLike);
        return;
      }
      setReplyTo({ email: emailLike, forward: true });
    } else if (selectedThread) {
      const messages = selectedThread.messages || [];
      const lastMessage = messages[messages.length - 1];
      if (lastMessage) {
        const chainHtml = buildThreadChainHtml(messages, lastMessage.message_id);
        const fwdLastMsgHtml = lastMessage.html_content || lastMessage.body_html || '';
        const fwdLastMsgText = lastMessage.text_content || lastMessage.body_text || '';
        const emailLike = buildComposerEmailFromMessage(
          lastMessage,
          selectedThread,
          (fwdLastMsgHtml || fwdLastMsgText || '') + chainHtml,
          fwdLastMsgText
        );
        if (onExternalForward) {
          onExternalForward(emailLike);
          return;
        }
        setReplyTo({ email: emailLike, forward: true });
      }
    }
    setPendingComposerFromAccount(getContextualFromAccount());
    setShowComposer(true);
  };

  // Handle reply with edited PDF attachment from ThreadViewer
  const handleThreadReplyWithAttachment = (message: ThreadMessage, file: File) => {
    // Build the email-like object from the message  
    const threadMessages = selectedThread?.messages || [];
    const chainHtml = buildThreadChainHtml(threadMessages, message.message_id);
    const msgHtml = message.html_content || message.body_html || '';
    const msgText = message.text_content || message.body_text || '';
    const emailLike = buildComposerEmailFromMessage(
      message,
      selectedThread,
      (msgHtml || msgText || '') + chainHtml,
      msgText
    );

    if (externalReplyWithAttachment) {
      // Let the parent handle it (page.tsx multi-compose)
      externalReplyWithAttachment(emailLike, file);
      return;
    }

    // Internal composer: create reply and inject attachment
    const composerId = internalCreateComposer({ email: emailLike, all: false }, getContextualFromAccount());
    if (composerId) {
      // Give the composer a tick to initialize, then add the attachment
      setTimeout(() => {
        updateComposer(composerId, { attachments: [file] });
      }, 100);
    }
  };

  // Handle forward
  const handleForward = (email: Email) => {
    if (onExternalForward) {
      onExternalForward(email);
      return;
    }
    setReplyTo({ email, forward: true });
    setPendingComposerFromAccount(getContextualFromAccount());
    setShowComposer(true);
  };

  // Handle editing a draft email
  const handleEditDraft = (email: Email) => {
    // For drafts, set the email content without replyTo context
    // The MultiComposeManager will need to extract to/cc/bcc/subject/body from the email
    setReplyTo({ email, isDraft: true });
    setPendingComposerFromAccount(getContextualFromAccount());
    setShowComposer(true);
  };

  // Handle double-click on thread to open fullscreen (now uses ThreadViewer's fullscreen mode)
  const handleDoubleClickThread = (thread: ThreadListItem) => {
    if (externalDoubleClickThread) {
      externalDoubleClickThread(thread);
      return;
    }
    // Select the thread first so ThreadViewer shows it, then set fullscreen
    selectThread(thread);
    setFullScreenThread(thread);
  };

  // Handle bulk actions
  const handleDeleteEmails = useCallback(async (items: ThreadListItem[]) => {
    const threadIds = Array.from(new Set(items.map((item) => item.thread_id).filter(Boolean)));
    if (threadIds.length === 0) {
      return;
    }

    if (threadIds.length === 1) {
      await deleteThread(threadIds[0]);
    } else {
      await deleteThreads(threadIds);
    }

    setSelectedThreadIds((prev) => prev.filter((threadId) => !threadIds.includes(threadId)));
  }, [deleteThread, deleteThreads]);

  const handleArchiveEmails = useCallback(async (items: ThreadListItem[]) => {
    const threadIds = Array.from(new Set(items.map((item) => item.thread_id).filter(Boolean)));
    if (threadIds.length === 0) {
      return;
    }

    const movedResults = await Promise.all(threadIds.map((threadId) => moveThread(threadId, 'Archive')));
    const moved = movedResults.every(Boolean);

    if (!moved) {
      await fetchThreads({
        folder: selectedFolder,
        accountId: activeSharedMailboxId ?? undefined,
        forceRefresh: true,
      });
      return;
    }

    await Promise.all([
      fetchThreadStats(),
      primeThreadFolderCache('Archive', activeSharedMailboxId ?? undefined),
    ]);

    setSelectedThreadIds((prev) => prev.filter((threadId) => !threadIds.includes(threadId)));
  }, [activeSharedMailboxId, fetchThreadStats, fetchThreads, moveThread, selectedFolder]);

  const handleMoveSelectedThreadsToFolder = useCallback(async (folderName: string, folderLabel: string) => {
    const threadIds = Array.from(new Set(selectedThreadIds.filter(Boolean)));
    if (threadIds.length === 0 || isBulkMoving) {
      return;
    }

    setIsBulkMoving(true);
    try {
      const movedResults = await Promise.all(threadIds.map((threadId) => moveThread(threadId, folderName)));
      const moved = movedResults.every(Boolean);

      if (!moved) {
        await fetchThreads({
          folder: selectedFolder,
          accountId: activeSharedMailboxId ?? undefined,
          forceRefresh: true,
        });
        return;
      }

      await Promise.all([
        fetchThreadStats(),
        primeThreadFolderCache(folderName, activeSharedMailboxId ?? undefined),
      ]);

      notify.success(
        threadIds.length === 1
          ? `Moved email to ${folderLabel}`
          : `Moved ${threadIds.length} emails to ${folderLabel}`
      );
      setShowBulkMoveDropdown(false);
      clearMultiSelection();
    } finally {
      setIsBulkMoving(false);
    }
  }, [activeSharedMailboxId, clearMultiSelection, fetchThreadStats, fetchThreads, isBulkMoving, moveThread, selectedFolder, selectedThreadIds]);

  const handleMoveSingleThread = useCallback(async (threadId: string, folderName: string) => {
    const success = await moveThread(threadId, folderName);
    if (success) {
      const folderLabel = threadMoveFolders.find((f) => f.name === folderName)?.label || folderName;
      notify.success(`Moved email to ${folderLabel}`);
      await Promise.all([
        fetchThreadStats(),
        primeThreadFolderCache(folderName, activeSharedMailboxId ?? undefined),
      ]);
    } else {
      await fetchThreads({
        folder: selectedFolder,
        accountId: activeSharedMailboxId ?? undefined,
        forceRefresh: true,
      });
    }
  }, [activeSharedMailboxId, fetchThreadStats, fetchThreads, moveThread, selectedFolder, threadMoveFolders]);

  const handleDeleteSelectedThreads = useCallback(async () => {
    if (selectedThreads.length === 0) {
      return;
    }

    await handleDeleteEmails(selectedThreads);
    clearMultiSelection();
  }, [clearMultiSelection, handleDeleteEmails, selectedThreads]);

  const handleArchiveSelectedThreads = useCallback(async () => {
    if (selectedThreads.length === 0) {
      return;
    }

    await handleArchiveEmails(selectedThreads);
    clearMultiSelection();
  }, [clearMultiSelection, handleArchiveEmails, selectedThreads]);

  // If no email account AND no shared mailbox selected AND no delegated accounts available
  // show admin message. Virtual users with shared mailbox access bypass this via auto-select effect.
  // Wait for initialization to complete to prevent flash of "no account" screen.
  if (emailInitComplete && !hasEmailAccount && !activeSharedMailboxId && delegatedAccounts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50 dark:bg-gray-900">
        <div className="text-center max-w-md p-8">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
            <Mail className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            No Email Account
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            Your administrator needs to create a mailbox for you. Contact your system administrator to get started.
          </p>
          <div className="flex items-center justify-center gap-2 text-sm text-gray-500 dark:text-gray-400">
            <AlertCircle className="w-4 h-4" />
            <span>Email accounts are managed by administrators</span>
          </div>
        </div>
      </div>
    );
  }

  // Mobile/Modal Layout
  if (isModal) {
    // Get the current mailbox display info
    const currentMailboxName = currentMailboxOption?.label || 'My Mailbox';
    const currentFolderLabel = selectedFolderDisplay.label;
    const CurrentFolderIcon = selectedFolderDisplay.icon;
    const folderMailboxContext: string | null = currentMailboxContext;
    const MobileMailboxIcon = selectedFolder === 'INBOX' && currentMailboxOption
      ? currentMailboxOption.icon
      : CurrentFolderIcon;
    const showMailboxContextPill = selectedFolder === 'INBOX' && currentMailboxOption?.type !== 'primary' && currentMailboxOption?.badgeLabel;
    return (
      <div className="flex flex-col h-full bg-white dark:bg-gray-900">
        {/* Mobile Header Bar with Combined Mailbox/Folder Dropdown and Compose */}
        {/* Now 2-row navigation: shared mailbox selection is integrated into folder dropdown */}
        {/* Only show these rows when in list view — hide when viewing a thread or replying */}
        {resolvedMobileView === 'list' && !suppressListToolbar && (
          <div className="border-b border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800">
            <div className="flex items-center gap-1.5 sm:gap-2 p-2 min-w-0">
              <div
                ref={folderDropdownRef}
                className="relative min-w-0 flex-shrink-0 w-1/2"
              >
                <button
                  type="button"
                  onClick={() => setShowFolderDropdown(!showFolderDropdown)}
                  className="flex items-center gap-1.5 px-2 py-1.5 bg-white dark:bg-gray-700 rounded-lg border border-gray-200 dark:border-gray-600 w-full min-w-0 touch-manipulation"
                >
                  <MobileMailboxIcon className="w-4 h-4 text-blue-600 flex-shrink-0" />
                  <span className="flex-1 text-left text-xs sm:text-sm font-medium truncate">
                    {selectedFolder === 'INBOX' ? currentMailboxName : currentFolderLabel}
                  </span>
                  {showMailboxContextPill && (
                    <span className="hidden sm:inline px-1.5 py-0.5 text-[10px] sm:text-xs bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded flex-shrink-0">
                      {currentMailboxOption?.badgeLabel}
                    </span>
                  )}
                  {selectedFolder === 'INBOX' && (currentMailboxOption?.unreadCount || 0) > 0 && (
                    <span className="px-1.5 py-0.5 text-[10px] font-medium bg-blue-600 text-white rounded-full flex-shrink-0">
                      {(currentMailboxOption?.unreadCount || 0) > 99 ? '99+' : currentMailboxOption?.unreadCount}
                    </span>
                  )}
                  <ChevronDown
                    className={cn('w-3.5 h-3.5 flex-shrink-0 transition-transform', showFolderDropdown && 'rotate-180')}
                  />
                </button>

                {showFolderDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-700 z-[200] max-h-[min(70vh,32rem)] overflow-y-auto">
                    <div className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 font-medium bg-gray-50 dark:bg-gray-900/50 border-b border-gray-200 dark:border-gray-700">
                      Mailboxes
                    </div>

                    {availableMailboxes.map((mailbox) => {
                      const MailboxIcon = mailbox.icon;
                      const isActive = currentMailboxContext === mailbox.contextId && selectedFolder === 'INBOX';
                      const secondaryLabel = mailbox.type === 'primary'
                        ? undefined
                        : mailbox.badgeLabel;

                      return (
                        <button
                          key={mailbox.contextId}
                          type="button"
                          onClick={() => {
                            handleSelectFolder('INBOX', mailbox.contextId);
                            setShowFolderDropdown(false);
                          }}
                          className={cn(
                            'flex items-center gap-2 w-full px-3 py-2.5 transition-colors',
                            isActive
                              ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                              : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
                          )}
                        >
                          <MailboxIcon className="w-4 h-4" />
                          <div className="flex-1 text-left min-w-0">
                            <div className="text-sm truncate">{mailbox.label}</div>
                            {secondaryLabel && (
                              <div className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {secondaryLabel}
                              </div>
                            )}
                          </div>
                          {mailbox.unreadCount > 0 && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-600 text-white rounded-full">
                              {mailbox.unreadCount > 99 ? '99+' : mailbox.unreadCount}
                            </span>
                          )}
                        </button>
                      );
                    })}

                    <div className="px-3 py-1.5 text-xs text-gray-500 dark:text-gray-400 font-medium bg-gray-50 dark:bg-gray-900/50 border-t border-b border-gray-200 dark:border-gray-700 mt-1">
                      Folders
                    </div>

                    {displayFolders
                      .filter((f) => f.name !== 'INBOX')
                      .map((folder, index) => {
                        const Icon = folder.icon;
                        const isActive =
                          selectedFolder === folder.name || selectedFolder === folder.label;

                        return (
                          <button
                            key={`${folder.name}-${index}`}
                            type="button"
                            onClick={() => {
                              const mb = folder.mailboxContext ?? folderMailboxContext;
                              handleSelectFolder(folder.name, mb);
                              setShowFolderDropdown(false);
                            }}
                            className={cn(
                              'flex items-center gap-2 w-full px-3 py-2.5 transition-colors',
                              isActive
                                ? 'bg-blue-50 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                                : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700',
                            )}
                          >
                            {folder.isCustom && folder.color ? (
                              <div
                                className="w-4 h-4 rounded-full flex-shrink-0"
                                style={{ backgroundColor: folder.color }}
                              />
                            ) : (
                              <Icon
                                className={cn('w-4 h-4', isActive ? 'text-blue-600 dark:text-blue-400' : '')}
                              />
                            )}
                            <span className="flex-1 text-left text-sm">{folder.label}</span>
                          </button>
                        );
                      })}
                  </div>
                )}
              </div>

              <form onSubmit={handleSearch} className="flex-1 min-w-0 w-1/2 flex items-center gap-1">
                <div className="relative flex-1 min-w-0">
                  <Search
                    className={cn(
                      'absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none',
                      searchQuery ? 'text-blue-500' : 'text-gray-400',
                    )}
                  />
                  <input
                    type="text"
                    placeholder="Search emails..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className={cn(
                      'h-9 sm:h-8 w-full rounded-lg border pl-7 bg-white dark:bg-gray-900 text-[13px] text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation',
                      searchQuery
                        ? 'pr-7 border-blue-300 dark:border-blue-600'
                        : 'pr-2 border-gray-200 dark:border-gray-600',
                    )}
                  />
                  {searchQuery && (
                    <button
                      type="button"
                      onClick={handleClearSearch}
                      className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 touch-manipulation"
                      title="Clear search"
                    >
                      <X className="w-3.5 h-3.5" />
                    </button>
                  )}
                </div>
                <div className="relative flex-shrink-0" ref={sortDropdownRef}>
                  <button
                    type="button"
                    onClick={() => setShowSortDropdown(!showSortDropdown)}
                    className={cn(
                      'h-9 sm:h-8 w-9 sm:w-8 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center transition-colors touch-manipulation',
                      showSortDropdown
                        ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400'
                        : 'text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-900',
                    )}
                    title="Sort emails"
                  >
                    <ArrowUpDown className="w-4 h-4" />
                  </button>
                  {showSortDropdown && (
                    <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-[200]">
                      <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                        <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">
                          Sort By
                        </span>
                      </div>
                      {[
                        { value: 'date', label: 'Date' },
                        { value: 'subject', label: 'Subject' },
                        { value: 'from', label: 'From' },
                      ].map((option) => (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            if (sortBy === option.value) {
                              setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                            } else {
                              setSortBy(option.value as 'date' | 'subject' | 'from');
                              setSortOrder('desc');
                            }
                          }}
                          className={cn(
                            'w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700',
                            sortBy === option.value && 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400',
                          )}
                        >
                          {option.label}
                          {sortBy === option.value && (
                            <span className="text-xs">
                              {sortOrder === 'desc' ? '↓ Newest' : '↑ Oldest'}
                            </span>
                          )}
                        </button>
                      ))}
                      <div className="p-2 border-t border-gray-200 dark:border-gray-700">
                        <button
                          type="button"
                          onClick={() => setShowSortDropdown(false)}
                          className="w-full px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                        >
                          Close
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}

        {resolvedMobileView === 'list' && selectedThreadIds.length > 0 && (
          <div className="px-3 py-2 border-b border-blue-200 dark:border-blue-800 bg-blue-50/80 dark:bg-blue-950/40 flex items-center justify-between gap-2 flex-wrap">
            <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
              {selectedThreadIds.length} selected
            </span>
            <div className="flex items-center gap-2 flex-wrap">
              {bulkMoveFolders.length > 0 && (
                <div className="relative" ref={bulkMoveDropdownRef}>
                  <button
                    onClick={() => setShowBulkMoveDropdown((current) => !current)}
                    disabled={isBulkMoving}
                    className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-gray-900 text-blue-700 dark:text-blue-300 border border-blue-200 dark:border-blue-800 disabled:opacity-60"
                  >
                    {isBulkMoving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FolderOpen className="w-3.5 h-3.5" />}
                    Move to Folder
                    <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', showBulkMoveDropdown && 'rotate-180')} />
                  </button>

                  {showBulkMoveDropdown && (
                    <div className="absolute left-0 top-full mt-1 z-[210] w-56 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden">
                      <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/60 border-b border-gray-200 dark:border-gray-700">
                        Move selected emails
                      </div>
                      <div className="max-h-72 overflow-y-auto py-1">
                        {bulkMoveFolders.map((folder) => (
                          <button
                            key={folder.name}
                            type="button"
                            onClick={() => void handleMoveSelectedThreadsToFolder(folder.name, folder.label)}
                            className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                          >
                            {folder.isCustom && folder.color ? (
                              <div className="h-3.5 w-3.5 rounded-full flex-shrink-0" style={{ backgroundColor: folder.color }} />
                            ) : (
                              <FolderOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />
                            )}
                            <span className="truncate">{folder.label}</span>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              {selectedFolder.toLowerCase() !== 'archive' && (
                <button
                  onClick={handleArchiveSelectedThreads}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-gray-900 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800"
                >
                  <Archive className="w-3.5 h-3.5" />
                  Move to Archive
                </button>
              )}
              <button
                onClick={handleDeleteSelectedThreads}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-gray-900 text-red-700 dark:text-red-300 border border-red-200 dark:border-red-800"
              >
                <Trash2 className="w-3.5 h-3.5" />
                Delete
              </button>
              <button
                onClick={clearMultiSelection}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-300 border border-gray-200 dark:border-gray-700"
              >
                <X className="w-3.5 h-3.5" />
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* Main Content Area */}
        <div className="flex-1 overflow-hidden">
          {resolvedMobileView === 'list' ? (
            // Email List View
            <div key="list-view" className="h-full flex flex-col animate-slide-in-left">
              {/* Outbox/Scheduled: Render OutboxList */}
              {(selectedFolder.toLowerCase() === 'outbox' || selectedFolder.toLowerCase() === 'scheduled') ? (
                <OutboxList
                  items={outboxItems}
                  loading={outboxLoading}
                  onCancel={cancelOutboxItem}
                  onRefresh={refreshOutbox}
                  mode={selectedFolder.toLowerCase() === 'scheduled' ? 'scheduled' : 'outbox'}
                />
              ) : (
              <>
              {/* Status Bar — hidden in modal mode (shown in EmailMobileModal header instead) */}
              {!isModal && (
              <div className="px-3 py-1.5 bg-gray-50 dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
                <span className="text-xs text-gray-500 flex items-center gap-1">
                  {isSearching 
                    ? `${displayThreads.length} search results` 
                    : `${displayThreads.length} conversations`}
                  <UserDocsLink docsPath="/total-connect/email" label="User guide: Email" />
                </span>
                <div className="flex items-center gap-2">
                  <span className={cn(
                    'w-2 h-2 rounded-full',
                    emailRealtimeStatus === 'live'
                      ? 'bg-green-500'
                      : emailRealtimeStatus === 'online'
                        ? 'bg-blue-500'
                        : 'bg-gray-400'
                  )} />
                  <span className="text-xs text-gray-400">
                    {emailRealtimeLabel}
                  </span>
                </div>
              </div>
              )}

              {/* Email/Thread List */}
              <div className="flex-1 overflow-y-auto">
                {threadsLoading ? (
                  <div className="divide-y divide-gray-100 dark:divide-gray-800">
                    {Array.from({ length: 6 }).map((_, i) => (
                      <div key={i} className="flex items-start gap-3 p-3 animate-pulse">
                        <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                        <div className="flex-1 min-w-0 space-y-2">
                          <div className="flex items-center justify-between gap-2">
                            <div className="h-3.5 w-24 rounded bg-gray-200 dark:bg-gray-700" />
                            <div className="h-3 w-12 rounded bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                          </div>
                          <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
                          <div className="h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
                        </div>
                      </div>
                    ))}
                  </div>
                ) : error ? (
                  <div className="p-4 text-center text-red-500">
                    <AlertCircle className="w-8 h-8 mx-auto mb-2" />
                    <p className="mb-2">{error}</p>
                    <button
                      onClick={() => fetchThreads({ forceRefresh: true })}
                      className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
                    >
                      <RefreshCw className="w-3.5 h-3.5" />
                      Retry
                    </button>
                  </div>
                ) : displayThreads.length === 0 ? (
                  <div className="p-4 text-center text-gray-500">
                    <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
                    <p>{isSearching ? 'No search results' : 'No conversations in this folder'}</p>
                  </div>
                ) : (
                  <ThreadList
                    threads={displayThreads}
                    selectedThread={selectedThread}
                    onSelectThread={handleOpenThreadFromList}
                    onDoubleClickThread={handleDoubleClickThread}
                    compactView={true}
                    currentFolder={selectedFolder}
                    isSharedMailbox={!!activeSharedMailboxId}
                    mailboxId={activeSharedMailboxId || primaryAccount?.id}
                    selectedThreadIds={selectedThreadIds}
                    multiSelectMode={multiSelectMode}
                    isMobileSelectionEnabled={isModal}
                    onToggleThreadSelection={handleToggleThreadSelection}
                    showSourceFilter={true}
                    onArchive={handleArchiveEmails}
                    onDelete={handleDeleteEmails}
                    onMoveThread={handleMoveSingleThread}
                    moveFolders={threadMoveFolders}
                    onViewWorkItemInTasks={onViewWorkItemInTasks}
                    hasMore={threadsHasMore}
                    onLoadMore={loadMoreThreads}
                    loading={threadsLoading}
                  />
                )}
              </div>
              </>
              )}
            </div>
          ) : (
            // Thread Viewer
            <div key="email-view" className="h-full flex flex-col animate-slide-in-right">
              {selectedThread ? (
                <ThreadViewer
                  thread={selectedThread}
                  onReply={handleThreadReply}
                  onReplyAll={handleThreadReplyAll}
                  onForward={handleThreadForward}
                  onBack={() => {
                    selectThread(null);
                    setMobileView('list');
                  }}
                  onArchive={(thread) => handleArchiveEmails([thread])}
                  onViewWorkItemInTasks={onViewWorkItemInTasks}
                  delegates={delegates}
                  isSharedMailbox={!!activeSharedMailboxId}
                  accountId={activeSharedMailboxId ?? undefined}
                  isFullscreen={!!fullScreenThread && fullScreenThread.thread_id === selectedThread.thread_id}
                  onToggleFullscreen={() => setFullScreenThread(fullScreenThread ? null : selectedThread)}
                  onClose={() => setFullScreenThread(null)}
                  onReplyWithAttachment={handleThreadReplyWithAttachment}
                  availableMoveFolders={threadMoveFolders}
                  inboxUnreadCount={activeSharedMailboxId ? (sharedMailboxUnreadCounts[activeSharedMailboxId] || 0) : inboxUnreadCount}
                />
              ) : (
                <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-800/50">
                  <button
                    onClick={() => setMobileView('list')}
                    className="text-blue-600 dark:text-blue-400 flex items-center gap-2"
                  >
                    <ChevronLeft className="w-5 h-5" />
                    Back to list
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Floating Action Button for Compose (Outlook-style) */}
        {resolvedMobileView === 'list' && selectedThreadIds.length === 0 && !hideComposer && (
          <button
            type="button"
            onClick={handleCompose}
            className="absolute bottom-4 right-4 w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-blue-600 hover:bg-blue-700 text-white shadow-lg flex items-center justify-center transition-transform active:scale-95 z-30 touch-manipulation"
            title="New email"
            aria-label="Compose new email"
          >
            <Plus className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>
        )}

        {/* Composer - hidden when using external composer */}
        {composers.length > 0 && !hideComposer && (
          <MultiComposeManager
            composers={composers}
            activeComposerId={activeComposerId}
            availableFromAccounts={availableFromAccounts}
            onUpdateComposer={updateComposer}
            onCloseComposer={closeComposer}
            onMinimizeComposer={minimizeComposer}
            onRestoreComposer={restoreComposer}
            onMaximizeComposer={maximizeComposer}
            onSent={(id) => {
              internalHandleSent(id);
              setReplyTo(null);
              if (selectedFolder === 'Sent' || selectedFolder === 'SENT') {
                fetchEmails(selectedFolder);
              }
            }}
          />
        )}
        
        {/* Settings Modal */}
        {showSettings && (
          <EmailSettingsModal
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            clientId={clientId}
            userId={userId}
            hasPrimaryMailbox={hasEmailAccount}
            delegatedAccounts={delegatedAccounts.map((a) => ({
              mailboxId: a.mailboxId,
              emailAddress: a.emailAddress,
              displayName: a.displayName,
              isShared: a.isShared ?? false,
              permissions: {
                canRead: true,
                canSend: a.permissions.canSend,
                canSendAs: a.permissions.canSendAs,
                canSendOnBehalf: a.permissions.canSendOnBehalf,
                canManage: false,
              },
              unreadCount: a.unreadCount,
            }))}
            onPreferencesChange={(prefs) => {
              setCombinedInboxEnabled(prefs.combinedInbox);
              setNullMailboxMode(prefs.combinedInbox ? 'combined' : 'primary');
            }}
          />
        )}
      </div>
    );
  }

  // Desktop Layout (original)
  return (
    <TotalConnectAccessGate featureName="Email">
    <div className="flex h-full overflow-hidden bg-white dark:bg-gray-900">
      {/* Folder Sidebar - hidden when using external navigation */}
      {!hideSidebar && (
      <div
        className={cn(
          'border-r border-gray-200 dark:border-gray-700 flex flex-col transition-all duration-200 bg-gray-50 dark:bg-gray-900',
          sidebarCollapsed ? 'w-16' : 'w-56'
        )}
      >
        {/* Compose Button */}
        <div className="p-3">
          <button
            onClick={handleCompose}
            className={cn(
              'flex items-center justify-center gap-2 w-full py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium shadow-sm',
              sidebarCollapsed ? 'px-2' : 'px-4'
            )}
          >
            <Plus className="w-5 h-5" />
            {!sidebarCollapsed && <span>Compose</span>}
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {availableMailboxes.length > 1 && (
            <div className="py-2">
              {!sidebarCollapsed && (
                <button
                  onClick={() => setMailboxesExpanded(!mailboxesExpanded)}
                  className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 w-full"
                >
                  {mailboxesExpanded ? (
                    <ChevronDown className="w-3.5 h-3.5" />
                  ) : (
                    <ChevronRight className="w-3.5 h-3.5" />
                  )}
                  Mailboxes
                </button>
              )}

              {(sidebarCollapsed || mailboxesExpanded) && (
                <nav className="px-2 space-y-0.5">
                  {availableMailboxes.map((mailbox) => {
                    const MailboxIcon = mailbox.icon;
                    const isActive = currentMailboxContext === mailbox.contextId;

                    return (
                      <button
                        key={mailbox.contextId}
                        onClick={() => handleSelectFolder('INBOX', mailbox.contextId)}
                        className={cn(
                          'flex items-center gap-2 w-full px-3 py-2 rounded-lg transition-colors',
                          isActive
                            ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                            : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                          sidebarCollapsed && 'justify-center'
                        )}
                        title={mailbox.label}
                      >
                        <MailboxIcon className={cn('w-4 h-4', isActive ? 'text-blue-600 dark:text-blue-400' : '')} />
                        {!sidebarCollapsed && (
                          <>
                            <div className="flex-1 min-w-0 text-left">
                              <div className="text-sm truncate">{mailbox.label}</div>
                              {mailbox.badgeLabel && (
                                <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
                                  {mailbox.badgeLabel}
                                </div>
                              )}
                            </div>
                            {mailbox.unreadCount > 0 && (
                              <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-600 text-white rounded-full">
                                {mailbox.unreadCount > 99 ? '99+' : mailbox.unreadCount}
                              </span>
                            )}
                          </>
                        )}
                      </button>
                    );
                  })}
                </nav>
              )}
            </div>
          )}

          {/* Folders Section */}
          <div className="py-2">
            {!sidebarCollapsed && (
              <button
                onClick={() => setFoldersExpanded(!foldersExpanded)}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 w-full"
              >
                {foldersExpanded ? (
                  <ChevronDown className="w-3.5 h-3.5" />
                ) : (
                  <ChevronRight className="w-3.5 h-3.5" />
                )}
                Folders
              </button>
            )}

            {(sidebarCollapsed || foldersExpanded) && (
              <nav className="px-2 space-y-0.5">
                {displayFolders.map((folder, index) => {
                  const Icon = folder.icon;
                  const isActive = selectedFolder === folder.name || selectedFolder === folder.label;
                  const count = folder.name === 'INBOX'
                    ? (activeSharedMailboxId
                        ? (sharedMailboxUnreadCounts[activeSharedMailboxId] || 0)
                        : inboxUnreadCount)
                    : 0;

                  return (
                    <button
                      key={`${folder.name}-${index}`}
                      onClick={() => handleSelectFolder(folder.name, folder.mailboxContext ?? undefined)}
                      className={cn(
                        'flex items-center gap-2 w-full px-3 py-2 rounded-lg transition-colors',
                        isActive
                          ? 'bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                        sidebarCollapsed && 'justify-center'
                      )}
                      title={folder.label}
                    >
                      {folder.isCustom && folder.color ? (
                        <div
                          className={cn('w-4 h-4 rounded-full flex-shrink-0', isActive && 'ring-2 ring-blue-400')}
                          style={{ backgroundColor: folder.color }}
                        />
                      ) : (
                        <Icon className={cn('w-4 h-4', isActive ? 'text-blue-600 dark:text-blue-400' : '')} />
                      )}
                      {!sidebarCollapsed && (
                        <>
                          <span className="flex-1 text-left text-sm">{folder.label}</span>
                          {count > 0 && (
                            <span className="px-1.5 py-0.5 text-xs font-medium bg-blue-600 text-white rounded-full">
                              {count}
                            </span>
                          )}
                        </>
                      )}
                    </button>
                  );
                })}
              </nav>
            )}
          </div>

          {!hideCrossModuleSidebarShortcuts ? (
            <>
              {/* Divider */}
              <div className="my-2 mx-3 border-t border-gray-200 dark:border-gray-700" />

              {/* Services Section */}
              <div className="py-2">
                {!sidebarCollapsed && (
                  <button
                    onClick={() => setServicesExpanded(!servicesExpanded)}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider hover:text-gray-700 dark:hover:text-gray-300 w-full"
                  >
                    {servicesExpanded ? (
                      <ChevronDown className="w-3.5 h-3.5" />
                    ) : (
                      <ChevronRight className="w-3.5 h-3.5" />
                    )}
                    Services
                  </button>
                )}

                {(sidebarCollapsed || servicesExpanded) && (
                  <nav className="px-2 space-y-0.5">
                    <button
                      onClick={onNavigateToCalendar}
                      className={cn(
                        'flex items-center gap-2 w-full px-3 py-2 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                        sidebarCollapsed && 'justify-center'
                      )}
                      title="Calendar"
                    >
                      <Calendar className="w-4 h-4 text-orange-500" />
                      {!sidebarCollapsed && <span className="text-sm">Calendar</span>}
                    </button>

                    <button
                      onClick={onNavigateToMeetings}
                      className={cn(
                        'flex items-center gap-2 w-full px-3 py-2 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                        sidebarCollapsed && 'justify-center'
                      )}
                      title="Online Meeting"
                    >
                      <Video className="w-4 h-4 text-purple-500" />
                      {!sidebarCollapsed && <span className="text-sm">Online Meeting</span>}
                    </button>

                    <button
                      onClick={onNavigateToChat}
                      className={cn(
                        'flex items-center gap-2 w-full px-3 py-2 rounded-lg transition-colors text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800',
                        sidebarCollapsed && 'justify-center'
                      )}
                      title="Chat"
                    >
                      <MessageCircle className="w-4 h-4 text-green-500" />
                      {!sidebarCollapsed && <span className="text-sm">Chat</span>}
                    </button>
                  </nav>
                )}
              </div>
            </>
          ) : null}
        </div>

        {/* Settings */}
        <div className="p-3 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={() => setShowSettings(true)}
            className={cn(
              'flex items-center gap-2 w-full py-2 px-3 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-lg transition-colors',
              sidebarCollapsed && 'justify-center'
            )}
            title="Settings"
          >
            <Settings className="w-5 h-5" />
            {!sidebarCollapsed && <span className="text-sm">Settings</span>}
          </button>
        </div>
      </div>
      )}

      {/* Email List - Fixed width for desktop split view */}
      <div
        data-tour-id="tcon-email-list"
        className="border-r border-gray-200 dark:border-gray-700 flex flex-col w-80 lg:w-96 flex-shrink-0 h-full overflow-hidden"
        style={externalListPanelWidth ? { width: externalListPanelWidth } : undefined}
      >
        {/* Search Bar */}
        <div data-tour-id="tcon-email-search" className="p-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <form onSubmit={handleSearch} className="flex-1 relative min-w-0">
              <Search
                className={cn(
                  'absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 pointer-events-none',
                  searchQuery ? 'text-blue-500' : 'text-gray-400',
                )}
              />
              <input
                type="text"
                placeholder="Search emails..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className={cn(
                  'h-8 w-full rounded-lg border bg-white dark:bg-gray-900 pl-7 text-[13px] text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent touch-manipulation',
                  searchQuery
                    ? 'pr-7 border-blue-300 dark:border-blue-600'
                    : 'pr-2 border-gray-200 dark:border-gray-600',
                )}
              />
              {(searchQuery || isSearching) && (
                <button
                  type="button"
                  onClick={() => {
                    setSearchQuery('');
                    setThreadsSearchQuery('');
                  }}
                  className="absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 touch-manipulation"
                  title="Clear search"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              )}
            </form>
            
            {/* Sort Dropdown */}
            <div className="relative" ref={sortDropdownRef}>
              <button
                type="button"
                onClick={() => setShowSortDropdown(!showSortDropdown)}
                className={cn(
                  "h-8 w-8 rounded-lg border border-gray-200 dark:border-gray-600 flex items-center justify-center transition-colors relative",
                  showSortDropdown || showUnreadOnly
                    ? "bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400"
                    : "text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-400 hover:bg-gray-100 dark:hover:bg-gray-700 bg-white dark:bg-gray-900"
                )}
                title={showUnreadOnly ? 'Sort & Filter (filter active)' : 'Sort emails'}
              >
                <ArrowUpDown className="w-4 h-4" />
                {showUnreadOnly && (
                  <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-500 rounded-full border-2 border-white dark:border-gray-900"></span>
                )}
              </button>
              {showSortDropdown && (
                <div className="absolute right-0 top-full mt-1 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50">
                  <div className="p-2 border-b border-gray-200 dark:border-gray-700">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Sort By</span>
                  </div>
                  {[
                    { value: 'date', label: 'Date', descLabel: '↓ Newest', ascLabel: '↑ Oldest' },
                    { value: 'subject', label: 'Subject', descLabel: '↓ Z-A', ascLabel: '↑ A-Z' },
                    { value: 'from', label: 'From', descLabel: '↓ Z-A', ascLabel: '↑ A-Z' },
                  ].map((option) => (
                    <button
                      key={option.value}
                      type="button"
                      onClick={() => {
                        if (sortBy === option.value) {
                          setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
                        } else {
                          setSortBy(option.value as 'date' | 'subject' | 'from');
                          setSortOrder('desc');
                        }
                      }}
                      className={cn(
                        "w-full px-3 py-2 text-left text-sm flex items-center justify-between hover:bg-gray-100 dark:hover:bg-gray-700",
                        sortBy === option.value && "bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400"
                      )}
                    >
                      {option.label}
                      {sortBy === option.value && (
                        <span className="text-xs">
                          {sortOrder === 'desc' ? option.descLabel : option.ascLabel}
                        </span>
                      )}
                    </button>
                  ))}
                  {/* Unread filter toggle */}
                  <div className="p-2 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => setShowUnreadOnly(!showUnreadOnly)}
                      className={cn(
                        "w-full px-3 py-2 text-left text-sm flex items-center justify-between rounded-lg",
                        showUnreadOnly 
                          ? "bg-blue-100 dark:bg-blue-900/40 text-blue-600 dark:text-blue-400" 
                          : "hover:bg-gray-100 dark:hover:bg-gray-700"
                      )}
                    >
                      <span>Unread Only</span>
                      {showUnreadOnly && <span className="text-xs">✓</span>}
                    </button>
                  </div>
                  <div className="p-2 border-t border-gray-200 dark:border-gray-700">
                    <button
                      type="button"
                      onClick={() => setShowSortDropdown(false)}
                      className="w-full px-3 py-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      Close
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Toolbar */}
        <div className="px-3 py-2 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
          <div className="flex items-center gap-2">
            {selectedThreadIds.length > 0 ? (
              <span className="text-sm font-medium text-blue-700 dark:text-blue-300">
                {selectedThreadIds.length} selected
              </span>
            ) : (
              <>
                <span className="text-sm text-gray-600 dark:text-gray-400">
                  {isSearching 
                    ? `${displayThreads.length} search results` 
                    : `${displayThreads.length} conversations`}
                </span>
                {showUnreadOnly && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-100 dark:bg-blue-900/40 text-blue-700 dark:text-blue-300 text-xs font-medium rounded-full">
                    <span className="w-1.5 h-1.5 bg-blue-500 rounded-full"></span>
                    Unread Only
                    <button
                      onClick={() => setShowUnreadOnly(false)}
                      className="ml-0.5 hover:text-blue-900 dark:hover:text-blue-100"
                      title="Clear filter"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                )}
                {/* Sync status indicator */}
                <div className="flex items-center gap-1" title={wsConnected ? 'Real-time sync active' : 'Sync disconnected'}>
                  <span className={cn(
                    'w-2 h-2 rounded-full',
                    wsConnected ? 'bg-green-500' : 'bg-gray-400'
                  )} />
                  {showSyncSuccess && (
                    <span className="flex items-center gap-1 text-xs text-green-600 dark:text-green-400">
                      <CheckCircle className="w-3 h-3" />
                      Synced
                    </span>
                  )}
                </div>
              </>
            )}
          </div>
          <div className="flex items-center gap-1.5">
            {selectedThreadIds.length > 0 && bulkMoveFolders.length > 0 && (
              <div className="relative" ref={bulkMoveDropdownRef}>
                <button
                  onClick={() => setShowBulkMoveDropdown((current) => !current)}
                  disabled={isBulkMoving}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-blue-700 dark:text-blue-300 hover:bg-blue-50 dark:hover:bg-blue-950/40 disabled:opacity-60"
                  title="Move selected emails to folder"
                >
                  {isBulkMoving ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <FolderOpen className="w-3.5 h-3.5" />}
                  Move to Folder
                  <ChevronDown className={cn('w-3.5 h-3.5 transition-transform', showBulkMoveDropdown && 'rotate-180')} />
                </button>

                {showBulkMoveDropdown && (
                  <div className="absolute right-0 top-full mt-1 z-[210] w-56 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-xl overflow-hidden">
                    <div className="px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400 bg-gray-50 dark:bg-gray-900/60 border-b border-gray-200 dark:border-gray-700">
                      Move selected emails
                    </div>
                    <div className="max-h-72 overflow-y-auto py-1">
                      {bulkMoveFolders.map((folder) => (
                        <button
                          key={folder.name}
                          type="button"
                          onClick={() => void handleMoveSelectedThreadsToFolder(folder.name, folder.label)}
                          className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                          {folder.isCustom && folder.color ? (
                            <div className="h-3.5 w-3.5 rounded-full flex-shrink-0" style={{ backgroundColor: folder.color }} />
                          ) : (
                            <FolderOpen className="w-4 h-4 text-gray-400 flex-shrink-0" />
                          )}
                          <span className="truncate">{folder.label}</span>
                        </button>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
            {selectedThreadIds.length > 0 && selectedFolder.toLowerCase() !== 'archive' && (
              <button
                onClick={handleArchiveSelectedThreads}
                className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-amber-700 dark:text-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/40"
                title="Move selected emails to Archive"
              >
                <Archive className="w-3.5 h-3.5" />
                Move to Archive
              </button>
            )}
            {selectedThreadIds.length > 0 && (
              <>
                <button
                  onClick={handleDeleteSelectedThreads}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-red-700 dark:text-red-300 hover:bg-red-50 dark:hover:bg-red-950/40"
                  title="Delete selected emails"
                >
                  <Trash2 className="w-3.5 h-3.5" />
                  Delete
                </button>
                <button
                  onClick={clearMultiSelection}
                  className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-xs font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800"
                  title="Cancel multi-select"
                >
                  <X className="w-3.5 h-3.5" />
                  Cancel
                </button>
              </>
            )}
            <button
              onClick={handleSync}
              disabled={syncStatus.syncing}
              className="p-1.5 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 disabled:opacity-50"
              title={syncButtonTitle}
            >
              <RefreshCw className={cn('w-4 h-4', syncStatus.syncing && 'animate-spin')} />
            </button>
          </div>
        </div>

        {/* Thread List / Outbox List */}
        <div className="flex-1 min-h-0 overflow-hidden">
          {(selectedFolder.toLowerCase() === 'outbox' || selectedFolder.toLowerCase() === 'scheduled') ? (
            <OutboxList
              items={outboxItems}
              loading={outboxLoading}
              onCancel={cancelOutboxItem}
              onRefresh={refreshOutbox}
              mode={selectedFolder.toLowerCase() === 'scheduled' ? 'scheduled' : 'outbox'}
            />
          ) : threadsLoading ? (
            <div className="divide-y divide-gray-100 dark:divide-gray-800">
              {Array.from({ length: 6 }).map((_, i) => (
                <div key={i} className="flex items-start gap-3 p-3 animate-pulse">
                  <div className="w-10 h-10 rounded-full bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                  <div className="flex-1 min-w-0 space-y-2">
                    <div className="flex items-center justify-between gap-2">
                      <div className="h-3.5 w-24 rounded bg-gray-200 dark:bg-gray-700" />
                      <div className="h-3 w-12 rounded bg-gray-200 dark:bg-gray-700 flex-shrink-0" />
                    </div>
                    <div className="h-3 w-3/4 rounded bg-gray-200 dark:bg-gray-700" />
                    <div className="h-3 w-1/2 rounded bg-gray-200 dark:bg-gray-700" />
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <div className="p-4 text-center text-red-500">
              <AlertCircle className="w-8 h-8 mx-auto mb-2" />
              <p className="mb-2">{error}</p>
              <button
                onClick={() => fetchThreads({ forceRefresh: true })}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-colors"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                Retry
              </button>
            </div>
          ) : displayThreads.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              <Inbox className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>{isSearching ? 'No search results' : 'No conversations yet'}</p>
            </div>
          ) : (
            <ThreadList
              threads={displayThreads}
              selectedThread={selectedThread}
              onSelectThread={handleOpenThreadFromList}
              onDoubleClickThread={handleDoubleClickThread}
              currentFolder={selectedFolder}
              isSharedMailbox={!!activeSharedMailboxId}
              mailboxId={activeSharedMailboxId || primaryAccount?.id}
              selectedThreadIds={selectedThreadIds}
              multiSelectMode={multiSelectMode}
              isMobileSelectionEnabled={false}
              onToggleThreadSelection={handleToggleThreadSelection}
              showSourceFilter={true}
              onArchive={handleArchiveEmails}
              onDelete={handleDeleteEmails}
              onMoveThread={handleMoveSingleThread}
              moveFolders={threadMoveFolders}
              onViewWorkItemInTasks={onViewWorkItemInTasks}
              hasMore={threadsHasMore}
              onLoadMore={loadMoreThreads}
              loading={threadsLoading}
            />
          )}
        </div>
      </div>

      {/* Thread Viewer */}
      <div data-tour-id="tcon-email-viewer" className="flex-1 flex flex-col min-w-0 h-full overflow-hidden">
        {selectedThread ? (
          <ThreadViewer
            thread={selectedThread}
            onReply={handleThreadReply}
            onReplyAll={handleThreadReplyAll}
            onForward={handleThreadForward}
            onBack={() => selectThread(null)}
            onArchive={(thread) => handleArchiveEmails([thread])}
            onViewWorkItemInTasks={onViewWorkItemInTasks}
            delegates={delegates}
            isSharedMailbox={!!activeSharedMailboxId}
            accountId={activeSharedMailboxId ?? undefined}
            isFullscreen={!!fullScreenThread && fullScreenThread.thread_id === selectedThread.thread_id}
            onToggleFullscreen={() => setFullScreenThread(fullScreenThread ? null : selectedThread)}
            onClose={() => setFullScreenThread(null)}
            onReplyWithAttachment={handleThreadReplyWithAttachment}
            availableMoveFolders={threadMoveFolders}
            inboxUnreadCount={activeSharedMailboxId ? (sharedMailboxUnreadCounts[activeSharedMailboxId] || 0) : inboxUnreadCount}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50 dark:bg-gray-800/50">
            <div className="text-center text-gray-500">
              <img src="/icons/email-icon.png" alt="Email" className="w-64 h-64 mx-auto mb-4" />
            </div>
          </div>
        )}
      </div>

      {/* Composer - hidden when using external composer */}
      {composers.length > 0 && !hideComposer && (
        <MultiComposeManager
          composers={composers}
          activeComposerId={activeComposerId}
          availableFromAccounts={availableFromAccounts}
          onUpdateComposer={updateComposer}
          onCloseComposer={closeComposer}
          onMinimizeComposer={minimizeComposer}
          onRestoreComposer={restoreComposer}
          onMaximizeComposer={maximizeComposer}
          onSent={(id) => {
            internalHandleSent(id);
            setReplyTo(null);
            if (selectedFolder === 'Sent' || selectedFolder === 'SENT') {
              fetchEmails(selectedFolder);
            }
          }}
        />
      )}

      {showSettings && (
        <EmailSettingsModal
          isOpen={showSettings}
          onClose={() => setShowSettings(false)}
          clientId={clientId}
          userId={userId}
          hasPrimaryMailbox={hasEmailAccount}
          delegatedAccounts={delegatedAccounts.map((a) => ({
            mailboxId: a.mailboxId,
            emailAddress: a.emailAddress,
            displayName: a.displayName,
            isShared: a.isShared ?? false,
            permissions: {
              canRead: true,
              canSend: a.permissions.canSend,
              canSendAs: a.permissions.canSendAs,
              canSendOnBehalf: a.permissions.canSendOnBehalf,
              canManage: false,
            },
            unreadCount: a.unreadCount,
          }))}
          onPreferencesChange={(prefs) => {
            setCombinedInboxEnabled(prefs.combinedInbox);
            setNullMailboxMode(prefs.combinedInbox ? 'combined' : 'primary');
          }}
        />
      )}

    </div>
    </TotalConnectAccessGate>
  );
}
